import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import { notesAPI, sensitiveAPI } from '../services/api'
import sensitiveNotesService from '../services/sensitiveNotesService'
import { useSensitiveSession } from './SensitiveSessionContext'
import { useFolders } from './FoldersContext'
import { useLocalMode } from './LocalModeContext'
import { useCloudStorage } from '../hooks/useCloudStorage'
import { useAuth } from './AuthContext'
import localStorageService from '../services/localStorageService'
import toast from 'react-hot-toast'
import { useStatus } from './StatusContext'
import frontendSync from '../services/frontendSync.js'
import { SYNC_CONFIG, shouldUseFrontendSync, logSyncConfig } from '../services/syncConfig.js'
import { detectNoteType, validateNoteTypeData } from '../utils/noteTypeDetection'
import { validateFlowchartData } from '../utils/flowchartUtils'

/**
 * Notes Context for managing notes state and operations.
 * 
 * Provides notes data, CRUD operations, and state management
 * across the entire application. Automatically switches between
 * server API and local storage based on current mode.
 * 
 * For cloud users, notes are cached in IndexedDB for offline access.
 */

const NotesContext = createContext({})

export const useNotes = () => {
  const context = useContext(NotesContext)
  if (!context) {
    throw new Error('useNotes must be used within a NotesProvider')
  }
  return context
}

export const NotesProvider = ({ children }) => {
  const [notes, setNotes] = useState([])
  const [loading, setLoading] = useState(false)
  const [showReconnectionBanner, setShowReconnectionBanner] = useState(false)
  const [frontendSyncInitialized, setFrontendSyncInitialized] = useState(false)
  const [googleDriveTokensAvailable, setGoogleDriveTokensAvailable] = useState(false)
  
  // Request deduplication for background sync
  const pendingSyncRef = useRef(null)
  const pendingSmartNavigationRef = useRef(null)
  const [pagination, setPagination] = useState({
    page: 0,
    size: 50, // Increased from 10 to 50 for better UX
    totalPages: 0,
    totalElements: 0,
    sortBy: 'lastModified',
    sortDir: 'desc'
  })

  // Real-time search state
  const [isSearchMode, setIsSearchMode] = useState(false)
  const [currentSearchTerm, setCurrentSearchTerm] = useState('')
  const [searchLoading, setSearchLoading] = useState(false)
  const [allNotes, setAllNotes] = useState([]) // Store original notes for search filtering

  // Background sort management
  const backgroundSortHandleRef = useRef(null)
  const isApplyingBackgroundSortRef = useRef(false)

  const getNoteTimestamp = (note) => {
    const value = note?.lastModified || note?.cloudModified || note?.modifiedTime || note?.updatedAt || note?.createdAt
    if (!value) return 0
    if (value instanceof Date) return value.getTime()
    const t = new Date(value).getTime()
    return isNaN(t) ? 0 : t
  }

  const arraysEqualByOrder = (a, b) => {
    if (a === b) return true
    if (!Array.isArray(a) || !Array.isArray(b)) return false
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      const idA = a[i]?.noteId || a[i]?.id
      const idB = b[i]?.noteId || b[i]?.id
      if (idA !== idB) return false
    }
    return true
  }

  useEffect(() => {
    // Don't sort during search mode to preserve search results
    if (isSearchMode && currentSearchTerm && currentSearchTerm.trim()) {
      console.log('⚠️ NotesContext: Skipping background sort during active search')
      return
    }

    if (!notes || notes.length < 2) return

    // Cancel any pending sort
    if (backgroundSortHandleRef.current) {
      if (typeof cancelIdleCallback !== 'undefined') {
        try { cancelIdleCallback(backgroundSortHandleRef.current) } catch (_) {}
      } else {
        clearTimeout(backgroundSortHandleRef.current)
      }
      backgroundSortHandleRef.current = null
    }

    const schedule = () => {
      const applySort = () => {
        if (isApplyingBackgroundSortRef.current) return
        // Double-check search mode before applying sort
        if (isSearchMode && currentSearchTerm && currentSearchTerm.trim()) {
          console.log('⚠️ NotesContext: Search mode active, aborting background sort')
          return
        }
        
        isApplyingBackgroundSortRef.current = true
        try {
          const sorted = [...notes].sort((a, b) => getNoteTimestamp(b) - getNoteTimestamp(a))
          if (!arraysEqualByOrder(notes, sorted)) {
            setNotes(sorted)
          }
        } finally {
          isApplyingBackgroundSortRef.current = false
        }
      }

      if (typeof requestIdleCallback !== 'undefined') {
        backgroundSortHandleRef.current = requestIdleCallback(applySort, { timeout: 500 })
      } else {
        backgroundSortHandleRef.current = setTimeout(applySort, 0)
      }
    }

    schedule()

    return () => {
      if (backgroundSortHandleRef.current) {
        if (typeof cancelIdleCallback !== 'undefined') {
          try { cancelIdleCallback(backgroundSortHandleRef.current) } catch (_) {}
        } else {
          clearTimeout(backgroundSortHandleRef.current)
        }
        backgroundSortHandleRef.current = null
      }
    }
  }, [notes, isSearchMode, currentSearchTerm])

  // Background sync timer and user activity tracking
  const [lastUserActivity, setLastUserActivity] = useState(Date.now())
  const [backgroundSyncTimer, setBackgroundSyncTimer] = useState(null)
  
  // Search debounce timer and cancellation
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null)
  const [currentSearchController, setCurrentSearchController] = useState(null)

  // Cleanup search timers and controllers on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer)
      }
      if (currentSearchController) {
        currentSearchController.abort()
      }
    }
  }, [])

  // Get contexts
  const { user, loading: authLoading } = useAuth()
  const sensitiveSession = useSensitiveSession()
  const { currentFolder, isShowingAllNotes, fetchFolders, folders } = useFolders()
  const { isConnected: isCloudConnected, disconnectProvider: disconnectCloudProvider, accessToken, userInfo } = useCloudStorage()
  const { showWarning, showError, showSuccess } = useStatus()
  const { 
    isLocalMode, 
    localNotes, 
    localFolders,
    loading: localLoading,
    createLocalNote, 
    updateLocalNote, 
    deleteLocalNote,
    searchLocalNotes,
          getSensitivePassword 
    } = useLocalMode()
  
    /**
     * Check if a note is sensitive based on its folder
     */
    const isNoteSensitive = useCallback((note, foldersData = null) => {
      if (!note.folderId) return false
      
      // Use provided folders data or fall back to current folders
      const availableFolders = foldersData || folders
      const noteFolder = availableFolders.find(f => f.folderId === note.folderId)
      return noteFolder?.folderType === 'SENSITIVE'
    }, [folders])
  
    /**
   * Handle Google Drive token expiration by showing reconnection prompt
   */
  const handleGoogleDriveTokenExpiration = useCallback(() => {
    // Prevent multiple simultaneous calls to avoid race conditions
    if (showReconnectionBanner) {
      console.log('🔔 NotesContext: Reconnection banner already shown, skipping duplicate call')
      return
    }
    
    console.log('🔔 NotesContext: Google Drive token expired, showing reconnection banner')
    
    // Show reconnection banner immediately
    setShowReconnectionBanner(true)
    
    // Clear access token but PRESERVE refresh token for automatic refresh
    localStorage.removeItem('cloudAccessToken')
    // DO NOT remove cloudRefreshToken - we need it for automatic token refresh!
    // localStorage.removeItem('cloudRefreshToken') // REMOVED - This breaks token refresh!
    localStorage.removeItem('lastSyncTime')
    
    console.log('🔐 NotesContext: Preserved cloudRefreshToken for automatic token refresh')
    
    // Show user-friendly message with reconnection guidance
    toast.error(
      '🔗 Google Drive connection lost. Please click "Reconnect" to restore sync.',
      { 
        duration: 10000,
        id: 'google-drive-reconnect' // Prevent duplicate toasts
      }
    )
    
    console.log('🔔 NotesContext: Reconnection banner shown, user should see "Reconnect" button')
  }, [disconnectCloudProvider, showReconnectionBanner])

  // Initialize IndexedDB for cloud cache (separate from local mode)
  useEffect(() => {
    const initCloudCache = async () => {
      if (!isLocalMode) {
        try {
          await localStorageService.initialize()
          console.log('💾 NotesContext: IndexedDB initialized for cloud cache')
        } catch (error) {
          console.warn('⚠️ NotesContext: Failed to initialize IndexedDB cache:', error)
        }
      }
    }
    
    initCloudCache()
  }, [isLocalMode])

  /**
   * Update a specific note in the cache with new data
   */
  const updateNoteInCache = async (noteId, updateData) => {
    try {
      const db = await openCloudCacheDB()
      const transaction = db.transaction(['cloud_notes'], 'readwrite')
      const store = transaction.objectStore('cloud_notes')
      
      // Get the existing note from cache
      const existingNote = await store.get(noteId)
      if (!existingNote) {
        console.log('📝 NotesContext: Note not found in cache, skipping cache update:', noteId)
        return
      }
      
      // Update the note with new data
      const updatedNote = {
        ...existingNote,
        ...updateData,
        cachedAt: new Date().toISOString()
      }
      
      await store.put(updatedNote)
      console.log(`💾 NotesContext: Updated note ${noteId} in cache with:`, updateData)
    } catch (error) {
      console.warn('⚠️ NotesContext: Failed to update note in cache:', error)
    }
  }

  // Listen for access token ready event and cloud reconnection events
  useEffect(() => {
    const handleAccessTokenReady = (event) => {
      console.log('🔑 NotesContext: Received accessTokenReady event')
      const { accessToken: eventAccessToken, userInfo: eventUserInfo } = event.detail
      
      // Skip initialization during onboarding to prevent unnecessary API calls
      const isOnboarding = window.location.pathname.includes('/onboarding')
      if (isOnboarding) {
        console.log('🔒 NotesContext: Onboarding detected, skipping frontend sync initialization from event')
        return
      }
      
      // Force re-initialization when token becomes available
      if (eventAccessToken && SYNC_CONFIG.USE_FRONTEND_SYNC && !frontendSyncInitialized) {
        // Show any cached cloud notes immediately for better UX
        ;(async () => {
          try {
            const cachedNotes = await getCachedCloudNotes()
            if (cachedNotes.length > 0) {
              updateStoredNotes(cachedNotes)
              setPagination(prev => ({
                ...prev,
                totalElements: cachedNotes.length,
                totalPages: Math.ceil(cachedNotes.length / prev.size)
              }))
            }
          } catch (_) {}
        })()
        console.log('🚀 NotesContext: Force initializing frontend sync from event')
        initializeFrontendSyncWithToken(eventAccessToken, eventUserInfo)
      }
    }
    
    const handleCloudReconnectionRequired = (event) => {
      console.log('🔔 NotesContext: Cloud reconnection required event received:', event.detail)
      const { provider, error, reason } = event.detail
      
      if (provider === 'google_drive') {
        console.log('🔄 NotesContext: Triggering Google Drive reconnection banner due to:', reason)
        handleGoogleDriveTokenExpiration()
      }
    }
    
    // Optimistic folder change for instant UI response
    const handleNoteFolderChanged = (event) => {
      const { noteId, folderId } = event.detail || {}
      if (!noteId) return
              setNotes(prev => prev.map(n => n.noteId === noteId ? { ...n, folderId: folderId || null } : n))
    }
    
    // Handle folders loaded event to re-process notes with sensitive filtering
    const handleFoldersLoaded = async (event) => {
      console.log('📁 NotesContext: Received foldersLoaded event - re-processing notes for sensitive filtering')
      const { folders } = event.detail
      console.log('📁 NotesContext: Folders now available:', folders.length, 'folders')
      
      // Re-process current notes to apply sensitive note filtering
      if (notes.length > 0 && SYNC_CONFIG.USE_FRONTEND_SYNC && frontendSync) {
        console.log('🔄 NotesContext: Re-processing', notes.length, 'notes with folder info')
        
        try {
          // Determine current folder ID using the same pattern as elsewhere in the code
          let folderId = null
          if (currentFolder?.folderId) {
            folderId = currentFolder.folderId
          } else {
            folderId = 'all' // Special value for all notes view
          }
          
          const reprocessedNotes = await frontendSync.decryptSensitiveNotesForDisplay(notes, folderId)
          console.log('✅ NotesContext: Re-processed notes with folder filtering:', reprocessedNotes.length, 'notes (', notes.length - reprocessedNotes.length, 'filtered out )')
          setNotes(reprocessedNotes)
        } catch (error) {
          console.error('❌ NotesContext: Error re-processing notes with folder info:', error)
        }
      }
    }
    
    // Handle local data cleared event to reset notes state
    const handleLocalDataCleared = () => {
      console.log('🧹 NotesContext: Received localDataCleared event - resetting notes state')
      setNotes([])
      setSearchResults([])
      setCurrentSearchTerm('')
      setIsSearchMode(false)
      setSyncInProgress(false)
      console.log('✅ NotesContext: Notes state reset after local data clearing')
    }
    
    // Handle note saved to cloud event to update note with cloudFileId
    const handleNoteSavedToCloud = (event) => {
      console.log('🔗 NotesContext: Received noteSavedToCloud event:', event.detail)
      const { tempNoteId, permanentNoteId, cloudFileId, note } = event.detail
      
      // Determine which note ID to update
      // Priority: tempNoteId (for UI display) > permanentNoteId (fallback)
      const noteIdToUpdate = tempNoteId || permanentNoteId
      
      if (!noteIdToUpdate) {
        console.warn('⚠️ NotesContext: No valid note ID found in event:', event.detail)
        return
      }
      
      console.log('🔍 NotesContext: Updating note with ID:', noteIdToUpdate, 'cloudFileId:', cloudFileId)
      
      // Update the note in the notes array with the cloudFileId (if available)
      setNotes(prev => prev.map(n => 
        n.noteId === noteIdToUpdate 
          ? { ...n, ...(cloudFileId && { cloudFileId }), justUploaded: true }
          : n
      ))
      
      console.log('✅ NotesContext: Updated note with ID:', noteIdToUpdate, cloudFileId ? `cloudFileId: ${cloudFileId}` : 'no cloudFileId')
      
      // Also update the cache if needed
      updateNoteInCache(noteIdToUpdate, { ...(cloudFileId && { cloudFileId }), justUploaded: true })
      
      // If we have both temp and permanent IDs, also update the permanent ID mapping
      if (tempNoteId && permanentNoteId && tempNoteId !== permanentNoteId) {
        console.log('🔄 NotesContext: Updating permanent ID mapping:', tempNoteId, '->', permanentNoteId)
        
        // Update the note with the permanent ID as well
        setNotes(prev => prev.map(n => 
          n.noteId === tempNoteId 
            ? { ...n, noteId: permanentNoteId, id: permanentNoteId, ...(cloudFileId && { cloudFileId }), justUploaded: true }
            : n
        ))
        
        // Update cache with permanent ID
        updateNoteInCache(permanentNoteId, { 
          noteId: permanentNoteId, 
          id: permanentNoteId, 
          ...(cloudFileId && { cloudFileId }), 
          justUploaded: true 
        })
      }
    }
    
    window.addEventListener('accessTokenReady', handleAccessTokenReady)
    window.addEventListener('cloudReconnectionRequired', handleCloudReconnectionRequired)
    window.addEventListener('noteFolderChanged', handleNoteFolderChanged)
    window.addEventListener('foldersLoaded', handleFoldersLoaded)
    window.addEventListener('localDataCleared', handleLocalDataCleared)
    window.addEventListener('noteSavedToCloud', handleNoteSavedToCloud)
    
    return () => {
      window.removeEventListener('accessTokenReady', handleAccessTokenReady)
      window.removeEventListener('cloudReconnectionRequired', handleCloudReconnectionRequired)
      window.removeEventListener('noteFolderChanged', handleNoteFolderChanged)
      window.removeEventListener('foldersLoaded', handleFoldersLoaded)
      window.removeEventListener('localDataCleared', handleLocalDataCleared)
      window.removeEventListener('noteSavedToCloud', handleNoteSavedToCloud)
    }
  }, [frontendSyncInitialized, handleGoogleDriveTokenExpiration, notes, currentFolder, frontendSync, updateNoteInCache])
  
  // Helper function to initialize frontend sync with explicit token
  const initializeFrontendSyncWithToken = async (tokenToUse, userInfoToUse) => {
    try {
      console.log('🌐 NotesContext: Initializing frontend sync with event token...')
      console.log('🔑 NotesContext: Event token length:', tokenToUse?.length)
      console.log('👤 NotesContext: Event user info:', userInfoToUse)
      
      await frontendSync.initialize(tokenToUse, userInfoToUse)
      setFrontendSyncInitialized(true)
      console.log('✅ NotesContext: Frontend sync service initialized successfully from event')
      
      // Show success message
      setTimeout(() => {
        if (typeof showSuccess === 'function') {
          showSuccess('🚀 Full sync optimization active! Reading & writing directly from Google Drive.')
        }
      }, 100)
    } catch (error) {
      console.error('❌ NotesContext: Failed to initialize frontend sync from event:', error)
    }
  }

  // Monitor Google Drive tokens availability
  useEffect(() => {
    const checkGoogleDriveTokens = () => {
      const hasTokens = !!(localStorage.getItem('cloudAccessToken') && localStorage.getItem('cloudRefreshToken'))
      if (hasTokens !== googleDriveTokensAvailable) {
        console.log('🔄 NotesContext: Google Drive tokens availability changed:', hasTokens)
        setGoogleDriveTokensAvailable(hasTokens)
      }
    }
    
    // Check immediately
    checkGoogleDriveTokens()
    
    // Listen for storage changes (from other tabs/windows)
    window.addEventListener('storage', checkGoogleDriveTokens)
    
    // Also check periodically for same-tab changes (fallback)
    const interval = setInterval(() => {
      // Stop checking once frontend sync is initialized
      if (frontendSyncInitialized) {
        clearInterval(interval)
        return
      }
      checkGoogleDriveTokens()
    }, 1000) // Check every second
    
    return () => {
      window.removeEventListener('storage', checkGoogleDriveTokens)
      clearInterval(interval)
    }
  }, [googleDriveTokensAvailable]) // Add dependency to prevent unnecessary re-runs

    // Initialize frontend sync when cloud storage is connected
  const initializeFrontendSync = useCallback(async () => {
    // Small delay to ensure CloudStorageContext state has propagated
    await new Promise(resolve => setTimeout(resolve, 100))
    console.log('🔄 NotesContext: Frontend sync initialization check...')
    console.log('  - isCloudConnected:', isCloudConnected)
    console.log('  - isLocalMode:', isLocalMode)
    console.log('  - accessToken available:', !!accessToken)
    console.log('  - accessToken length:', accessToken?.length)
    console.log('  - SYNC_CONFIG.USE_FRONTEND_SYNC:', SYNC_CONFIG.USE_FRONTEND_SYNC)
    console.log('  - googleDriveTokensAvailable:', googleDriveTokensAvailable)
    console.log('  - localStorage cloudAccessToken:', !!localStorage.getItem('cloudAccessToken'))
    console.log('  - localStorage cloudRefreshToken:', !!localStorage.getItem('cloudRefreshToken'))
    
    // Skip initialization during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 NotesContext: Onboarding detected, skipping frontend sync initialization')
      return
    }
    
    // Check if we have Google Drive tokens directly (for fresh device login)
    const hasGoogleDriveTokens = localStorage.getItem('cloudAccessToken') && localStorage.getItem('cloudRefreshToken')
    
    // Update state if tokens are available
    if (hasGoogleDriveTokens && !googleDriveTokensAvailable) {
      console.log('🔄 NotesContext: Updating Google Drive tokens availability state')
      setGoogleDriveTokensAvailable(true)
    }
    
    if ((isCloudConnected || hasGoogleDriveTokens) && !isLocalMode) {
      try {
        console.log('🔄 NotesContext: Enabling frontend-only sync to reduce backend load...')
        
        if ((accessToken || hasGoogleDriveTokens) && SYNC_CONFIG.USE_FRONTEND_SYNC && !frontendSyncInitialized) {
          console.log('🌐 NotesContext: Initializing frontend sync service with access token...')
          
          // Use Google Drive access token if JWT token is not available
          const tokenToUse = accessToken || localStorage.getItem('cloudAccessToken')
          console.log('🔑 NotesContext: Access token length:', tokenToUse?.length)
          console.log('👤 NotesContext: User info:', userInfo)
          await frontendSync.initialize(tokenToUse, userInfo)
          setFrontendSyncInitialized(true)
          console.log('✅ NotesContext: Frontend sync service initialized successfully')
          // After init, immediately load notes to populate UI
          try {
            const fresh = await frontendSync.loadNotes({ forceRefresh: true, pageSize: 50 })
            if (fresh?.notes?.length) {
              updateStoredNotes(fresh.notes)
              setPagination(prev => ({
                ...prev,
                totalElements: fresh.notes.length,
                totalPages: Math.ceil((fresh.notes.length || 0) / prev.size)
              }))
            }
          } catch (_) {}
        } else {
          if (isCloudConnected && !accessToken) {
            console.log('⏳ NotesContext: Cloud connected but waiting for access token...')
            // Don't show warning yet - token might still be loading
            return
          }
          if (frontendSyncInitialized) {
            console.log('✅ NotesContext: Frontend sync already initialized, skipping')
            return
          }
          console.log('⚠️ NotesContext: Frontend sync not initialized:')
          console.log('  - Access token available:', !!accessToken)
          console.log('  - Access token value (first 10 chars):', accessToken?.substring(0, 10))
          console.log('  - Frontend sync enabled:', SYNC_CONFIG.USE_FRONTEND_SYNC)
          console.log('  - Already initialized:', frontendSyncInitialized)
        }
        
        // Frontend sync will dramatically reduce backend Google Drive API calls
        // All sync operations will happen directly in the browser
        const syncConfig = SYNC_CONFIG.USE_FRONTEND_SYNC ? 'FRONTEND-ONLY' : 'BACKEND'
        console.log(`✅ NotesContext: Sync mode set to ${syncConfig} - backend load reduced by ~90%!`)
        console.log('🌐 NotesContext: Read AND write operations will bypass backend API')
        
        // Log detailed sync configuration
        logSyncConfig()
        
        // Show success message after a short delay to ensure status context is ready
        setTimeout(() => {
          if (typeof showSuccess === 'function') {
            showSuccess('🚀 Full sync optimization active! Reading & writing directly from Google Drive.')
          }
        }, 100)
        
      } catch (error) {
        console.error('❌ NotesContext: Failed to initialize frontend sync:', error)
        
        // Show warning after a short delay to ensure status context is ready
        setTimeout(() => {
          if (typeof showWarning === 'function') {
            showWarning('⚠️ Using legacy sync mode')
          }
        }, 100)
      }
    } else {
      // Reset initialization state when not connected
      if (frontendSyncInitialized) {
        console.log('🔄 NotesContext: Resetting frontend sync initialization state')
        setFrontendSyncInitialized(false)
      }
    }
  }, [isCloudConnected, isLocalMode, accessToken, userInfo, frontendSyncInitialized, googleDriveTokensAvailable])

  // Initialize frontend sync when cloud storage is connected
  useEffect(() => {
    initializeFrontendSync()
  }, [isCloudConnected, isLocalMode, accessToken, userInfo, frontendSyncInitialized, googleDriveTokensAvailable])

  // Additional effect to ensure initialization runs when tokens become available
  useEffect(() => {
    if (googleDriveTokensAvailable && !frontendSyncInitialized && SYNC_CONFIG.USE_FRONTEND_SYNC && !isLocalMode) {
      console.log('🚀 NotesContext: Google Drive tokens became available, triggering frontend sync initialization...')
      // Small delay to ensure state is fully updated
      setTimeout(() => {
        initializeFrontendSync()
      }, 100)
    }
  }, [googleDriveTokensAvailable, frontendSyncInitialized, isLocalMode])

  // Set up background sync and user activity tracking - OPTIMIZED
  useEffect(() => {
    if (isLocalMode) return // No background sync needed for local mode

    // PERFORMANCE FIX: Debounce activity tracking to prevent excessive state updates
    let activityTimeout = null
    const updateActivity = () => {
      // Clear previous timeout to debounce rapid events
      if (activityTimeout) {
        clearTimeout(activityTimeout)
      }
      
      // Only update state every 2 seconds to prevent excessive re-renders
      activityTimeout = setTimeout(() => {
        setLastUserActivity(Date.now())
      }, 2000) // 2 second debounce
    }
    
    // PERFORMANCE FIX: Reduce event types and use passive listeners
    const events = ['mousedown', 'keypress', 'scroll'] // Removed mousemove and touchstart
    events.forEach(event => document.addEventListener(event, updateActivity, { passive: true }))

    // Set up background sync timer (every 5 minutes)
    const timer = setInterval(async () => {
      const timeSinceActivity = Date.now() - lastUserActivity
      const isIdle = timeSinceActivity > 2 * 60 * 1000 // User idle for 2+ minutes
      
      if (isIdle && isCloudConnected) {
        // Skip background refresh if frontend sync is handling everything
        if (shouldUseFrontendSync('read')) {
          console.log('🚀 NotesContext: Frontend sync enabled - skipping backend background refresh')
          return
        }
        
        console.log('🔄 NotesContext: User idle, performing background cache refresh...')
        await backgroundCacheRefresh()
      }
    }, 5 * 60 * 1000) // Check every 5 minutes

    setBackgroundSyncTimer(timer)

    // Cleanup
    return () => {
      events.forEach(event => document.removeEventListener(event, updateActivity, { passive: true }))
      if (timer) clearInterval(timer)
      if (activityTimeout) clearTimeout(activityTimeout)
    }
  }, [isLocalMode, isCloudConnected]) // Removed lastUserActivity from dependencies to prevent infinite loop

  /**
   * Background cache refresh (non-blocking, silent sync)
   */
  const backgroundCacheRefresh = async () => {
    try {
      console.log('🔄 NotesContext: Starting background cache refresh...')
      
      // Get current folder context for background sync
      let folderId = null
      if (!isShowingAllNotes && currentFolder) {
        folderId = currentFolder.folderId
      } else if (isShowingAllNotes) {
        folderId = 'all'
      }

      // Use frontend sync for background refresh in cloud mode
      let freshNotes = []
      if (shouldUseFrontendSync('read') && frontendSync.isInitialized) {
        const frontendResponse = await frontendSync.loadNotes({
          folderId,
          pageSize: 50,
          sortBy: 'lastModified',
          sortDir: 'desc'
        })
        freshNotes = frontendResponse.notes || []
      } else {
        // Fallback to backend only if frontend-only mode is not active
        if (!shouldUseFrontendSync('read')) {
          const response = await notesAPI.getNotes({ 
            page: 0, 
            size: 50,
            sortBy: 'lastModified', 
            sortDir: 'desc',
            folderId 
          })
          freshNotes = response?.notes || []
        } else {
          console.log('⏭️ NotesContext: Skipping backend background refresh in frontend-only mode')
        }
      }
      
      if (freshNotes.length > 0) {
        // Update cache silently (don't update UI to avoid disrupting user)
        await cacheNotesInIndexedDB(freshNotes)
        console.log(`✅ NotesContext: Background cache refresh completed - cached ${freshNotes.length} notes`)
      }
    } catch (error) {
      // Silent failure for background sync - don't disrupt user experience
      console.log('🔄 NotesContext: Background cache refresh failed (silent):', error.message)
    }
  }

  // Sync local notes with notes state when in local mode
  useEffect(() => {
    if (isLocalMode && !localLoading) {
      // Don't update notes during active search to preserve search results
      if (isSearchMode && currentSearchTerm && currentSearchTerm.trim()) {
        console.log('⚠️ NotesContext: Skipping local notes update during active search')
        return
      }

      // Only fetch when local mode is ready and not loading
      console.log('🔍 NotesContext: Local mode ready, updating notes state')
      console.log('🔍 NotesContext: useEffect triggered by changes in dependencies:')
      console.log('  - isLocalMode:', isLocalMode)
      console.log('  - localLoading:', localLoading)
      console.log('  - localNotes.length:', localNotes?.length)
      console.log('  - currentFolder:', currentFolder?.name || 'null')
      console.log('  - isShowingAllNotes:', isShowingAllNotes)
      
      // Apply folder filtering for local notes
      let filteredNotes = localNotes || []
      
      if (!isShowingAllNotes && currentFolder) {
        // Filter by specific folder
        filteredNotes = localNotes.filter(note => note.folderId === currentFolder.folderId)
      } else if (isShowingAllNotes) {
        // Show all notes except sensitive ones (same as backend behavior)
        filteredNotes = localNotes.filter(note => !isNoteSensitive(note, localFolders))
      }
      
      // Update stored notes for search functionality
      updateStoredNotes(filteredNotes)
      
      // Update pagination
      setPagination(prev => ({
        ...prev,
        totalElements: filteredNotes.length,
        totalPages: Math.ceil(filteredNotes.length / prev.size),
        currentPage: 0
      }))
    }
  }, [isLocalMode, localNotes, localFolders, currentFolder, isShowingAllNotes, localLoading, isSearchMode, currentSearchTerm])

  // Fetch notes when current folder changes (for server mode)
  useEffect(() => {
    if (isLocalMode) return // Local mode handles its own data loading
    if (authLoading) {
      console.log('🔄 NotesContext: Waiting for authentication to complete before fetching notes...')
      return // Wait for auth to complete
    }
    if (!user) {
      console.log('🔄 NotesContext: No authenticated user, skipping notes fetch')
      return // User not authenticated
    }

    // ENHANCED: Handle sensitive folders with dedicated logic
    if (currentFolder?.folderType === 'SENSITIVE') {
      console.log('🔐 NotesContext: Sensitive folder detected, using local storage only')
      // Sensitive folders are handled by the dedicated useEffect below
      return
    }

    // Determine folderId based on current context
    let folderId = null
    
    if (!isShowingAllNotes && currentFolder) {
      folderId = currentFolder.folderId
    } else if (isShowingAllNotes) {
      folderId = 'all' // Special value for all notes except sensitive
    }
    
    console.log('🔍 NotesContext: Folder changed, implementing smart navigation...')
    
    // SMART NAVIGATION: Show cached notes instantly, sync only if needed
    handleSmartFolderNavigation(folderId)
  }, [currentFolder, isShowingAllNotes, isLocalMode, authLoading, user])

  // Load encrypted sensitive notes from local storage when sensitive folder is selected
  useEffect(() => {
    const loadSensitiveNotes = async () => {
      if (isLocalMode) return // Local mode handles its own data
      if (!currentFolder || currentFolder.folderType !== 'SENSITIVE') return

      try {
        console.log('🔐 NotesContext: Loading encrypted sensitive notes for folder:', currentFolder.folderId)
        setLoading(true)
        
        // Load encrypted notes for list display (no decryption)
        await loadEncryptedSensitiveNotes()
        
      } catch (error) {
        console.error('❌ NotesContext: Failed to load encrypted sensitive notes:', error)
        setNotes([])
        setPagination(prev => ({ ...prev, page: 0, totalElements: 0, totalPages: 0 }))
      } finally {
        setLoading(false)
      }
    }

    loadSensitiveNotes()
  }, [currentFolder, isLocalMode])

  // Hide reconnection banner when cloud connection is restored
  useEffect(() => {
    if (isCloudConnected && showReconnectionBanner && user && !authLoading) {
      console.log('🔄 NotesContext: Cloud connection restored, hiding reconnection banner')
      setShowReconnectionBanner(false)
      // Refresh notes to get fresh data
      fetchNotes({ forceSync: true })
    }
  }, [isCloudConnected, showReconnectionBanner, user, authLoading])

  /**
   * Fetch notes with optimistic loading: show cached notes immediately, then fresh notes from cloud
   * ENHANCED: Smart navigation mode to prevent unnecessary network calls
   */
  const fetchNotes = async (params = {}) => {
    if (isLocalMode) {
      // In local mode, we don't need to fetch anything - useEffect handles it
      return
    }

    // ENHANCED: Check if this is a folder navigation request
    // If so, we should use smart navigation instead of traditional sync
    if (params.folderId && !params.forceSync) {
      console.log('⚡ NotesContext: Folder navigation detected, redirecting to smart navigation')
      return handleSmartFolderNavigation(params.folderId)
    }

    // Prevent duplicate concurrent requests
    if (pendingSyncRef.current) {
      console.log('🔄 NotesContext: Sync already in progress, skipping duplicate request')
      return await pendingSyncRef.current
    }

    setLoading(true)

    // Create and track the sync promise
    const syncPromise = performSync(params)
    pendingSyncRef.current = syncPromise

    try {
      return await syncPromise
    } finally {
      pendingSyncRef.current = null
      setLoading(false)
    }
  }

  /**
   * Load more notes (append next page to existing notes)
   */
  const loadMore = async () => {
    if (isLocalMode) return // Local mode loads all notes at once
    if (isSearchMode) return // Don't load more during search
    if (loading) return // Don't load if already loading
    if (pagination.totalPages === 0) return // No pagination data yet
    if (pagination.page >= pagination.totalPages - 1) return // No more pages

    setLoading(true)

    try {
      const nextPage = pagination.page + 1
      console.log(`📄 NotesContext: Loading more notes - page ${nextPage + 1}/${pagination.totalPages}`)

      // Determine folderId based on current context
      let folderId = null
      if (!isShowingAllNotes && currentFolder) {
        folderId = currentFolder.folderId
      } else if (isShowingAllNotes) {
        folderId = 'all'
      }

      const params = {
        page: nextPage,
        size: pagination.size,
        sortBy: pagination.sortBy,
        sortDir: pagination.sortDir,
        ...(folderId && { folderId })
      }

      let response
      if (shouldUseFrontendSync('read')) {
        // Frontend sync loads all at once, so no "load more" needed
        console.log('🚀 NotesContext: Frontend sync already loaded all notes')
        return
      } else {
        response = await notesAPI.getNotes(params)
      }

      if (response?.notes?.length > 0) {
        // Append new notes to existing ones (deduplicating by ID)
        setNotes(prev => {
          const existingIds = new Set(prev.map(n => n.noteId || n.id))
          const newNotes = response.notes.filter(n => !existingIds.has(n.noteId || n.id))
          return [...prev, ...newNotes]
        })

        // Update pagination state
        setPagination(prev => ({
          ...prev,
          page: nextPage,
          totalPages: response.totalPages || prev.totalPages,
          totalElements: response.totalElements || prev.totalElements
        }))

        // Cache the new notes
        await cacheNotesInIndexedDB(response.notes)
        
        console.log(`✅ NotesContext: Loaded ${response.notes.length} more notes (page ${nextPage + 1})`)
        
        // Show success feedback
        toast.success(`Loaded ${response.notes.length} more notes`, {
          duration: 2000,
          position: 'bottom-center'
        })
      }

    } catch (error) {
      console.error('❌ NotesContext: Error loading more notes:', error)
      toast.error('Failed to load more notes')
    } finally {
      setLoading(false)
    }
  }

  /**
   * SMART FOLDER NAVIGATION: Show cached notes instantly, sync only if needed
   * This eliminates unnecessary network calls when switching between folders
   */
  const handleSmartFolderNavigation = async (folderId) => {
    // ENHANCED: Prevent multiple simultaneous executions
    if (pendingSmartNavigationRef.current) {
      console.log('🔄 NotesContext: Smart navigation already in progress, skipping duplicate request')
      return await pendingSmartNavigationRef.current
    }

    try {
      console.log('🚀 NotesContext: Starting smart folder navigation for:', folderId)
      
      // Create and track the smart navigation promise
      const smartNavPromise = performSmartNavigation(folderId)
      pendingSmartNavigationRef.current = smartNavPromise
      
      return await smartNavPromise
    } finally {
      pendingSmartNavigationRef.current = null
    }
  }

  const performSmartNavigation = async (folderId) => {
    try {
      console.log('🚀 NotesContext: Starting smart folder navigation for:', folderId)
      
      // Step 1: Show cached notes instantly for snappy UX
      console.log('💾 NotesContext: Loading cached notes for instant display...')
      let cachedNotes = []
      
      if (shouldUseFrontendSync('read')) {
        try {
          const frontendCached = await frontendSync.getCachedNotes(folderId)
          cachedNotes = Array.isArray(frontendCached) ? frontendCached : []
          console.log(`✅ NotesContext: Found ${cachedNotes.length} frontend cached notes`)
        } catch (e) {
          console.warn('⚠️ NotesContext: Failed to load frontend cached notes, falling back to cloud cache', e)
          cachedNotes = await getCachedCloudNotes()
        }
      } else {
        cachedNotes = await getCachedCloudNotes()
      }
      
      // Step 2: Filter cached notes by folder and show immediately
      if (cachedNotes.length > 0) {
        let filteredNotes = cachedNotes
        
        // Apply folder filtering
        if (folderId && folderId !== 'all') {
          filteredNotes = cachedNotes.filter(note => (note.folderId || null) === folderId)
          console.log(`🔍 NotesContext: Filtered ${cachedNotes.length} cached notes to ${filteredNotes.length} for folder ${folderId}`)
        } else if (folderId === 'all') {
          // For "all notes", exclude sensitive notes
          filteredNotes = cachedNotes.filter(note => note.folderType !== 'SENSITIVE')
          console.log(`🔍 NotesContext: Filtered ${cachedNotes.length} cached notes to ${filteredNotes.length} for all notes (excluding sensitive)`)
        }
        
        // Show filtered notes instantly
        if (filteredNotes.length > 0) {
          console.log(`⚡ NotesContext: Showing ${filteredNotes.length} cached notes instantly`)
          updateStoredNotes(filteredNotes)
          
          // Set pagination for cached notes
          setPagination(prev => ({
            ...prev,
            page: 0, // Reset to first page
            totalElements: filteredNotes.length,
            totalPages: Math.ceil(filteredNotes.length / prev.size),
            size: prev.size
          }))
          
          setLoading(false)
        } else {
          console.log('📝 NotesContext: No cached notes found for this folder')
          setNotes([])
          setPagination(prev => ({ ...prev, page: 0, totalElements: 0, totalPages: 0 }))
          setLoading(false)
        }
      } else {
        console.log('📝 NotesContext: No cached notes available')
        setNotes([])
        setPagination(prev => ({ ...prev, page: 0, totalElements: 0, totalPages: 0 }))
        setLoading(false)
      }
      
      // Step 3: Track folder change for smart sync decisions
      localStorage.setItem('lastFolderChange', Date.now().toString())
      
      // Step 4: Smart background sync - only if cache is stale or incomplete
      const shouldBackgroundSync = await shouldPerformBackgroundSync(cachedNotes)
      if (shouldBackgroundSync) {
        console.log('🔄 NotesContext: Cache is stale, performing background sync...')
        // Use a small delay to ensure UI updates are rendered first
        setTimeout(() => {
          fetchNotes({ folderId, page: 0, forceSync: true })
        }, 100)
      } else {
        console.log('⚡ NotesContext: Cache is fresh, skipping background sync')
        
        // Even if we skip sync, ensure we have the latest data for this specific folder
        // This handles cases where notes were created/updated in other folders
        if (cachedNotes.length > 0) {
          console.log('🔄 NotesContext: Performing minimal folder-specific refresh...')
          setTimeout(() => {
            performMinimalFolderRefresh(folderId)
          }, 500) // Small delay to not interfere with UI
        }
      }
      
    } catch (error) {
      console.error('❌ NotesContext: Error in smart folder navigation:', error)
      // Fallback to traditional sync on error
      console.log('🔄 NotesContext: Falling back to traditional sync due to error')
      fetchNotes({ folderId, page: 0, forceSync: true })
    }
  }

  /**
   * Perform minimal folder-specific refresh without full sync
   * This function is designed to be truly minimal - no network calls, just cache updates
   */
  const performMinimalFolderRefresh = async (folderId) => {
    try {
      console.log('🔄 NotesContext: Performing minimal folder refresh for:', folderId)
      
      // TRULY MINIMAL: Just update the UI state without any network calls
      // The notes are already in cache, we just need to ensure the UI reflects the current folder
      
      if (shouldUseFrontendSync('read')) {
        // For frontend sync, just ensure the current notes are properly filtered
        // Don't call getCachedNotes again as it might trigger background refresh
        console.log('⚡ NotesContext: Frontend sync mode - skipping minimal refresh to avoid network calls')
        return
      } else {
        // For backend sync, we could do a light refresh, but let's skip it for now
        // to ensure we don't make unnecessary network calls
        console.log('⚡ NotesContext: Backend sync mode - skipping minimal refresh to avoid network calls')
        return
      }
      
      // NOTE: The original implementation was calling frontendSync.getCachedNotes() 
      // which triggered background cache refresh and Google Drive API calls.
      // We've removed that to ensure truly minimal operation.
      
    } catch (error) {
      console.warn('⚠️ NotesContext: Minimal folder refresh failed, continuing with cached data:', error)
      // Don't throw - this is a background operation that shouldn't break the UI
    }
  }

  /**
   * Actual sync implementation (separated for deduplication)
   */
  const performSync = async (params = {}) => {
    try {
      // ENHANCED: Block sync for sensitive folders only in local mode
      if (currentFolder?.folderType === 'SENSITIVE' && isLocalMode) {
        console.log('🚫 NotesContext: BLOCKING sync for sensitive folder - local mode active')
        return
      }
      
      // In cloud mode, sensitive notes should sync to Google Drive
      if (currentFolder?.folderType === 'SENSITIVE' && !isLocalMode) {
        console.log('🔐 NotesContext: Syncing sensitive folder to cloud storage')
      }
      
      // For cloud users: Show cached notes immediately for snappy UX
      if (!isLocalMode) {
        console.log('💾 NotesContext: Loading cached notes first for instant display...')
        let cachedNotes = []
        if (shouldUseFrontendSync('read')) {
          try {
            // Determine folderId based on current context (same logic as fresh fetch)
            let folderId = params.folderId
            if (folderId === undefined) {
              if (!isShowingAllNotes && currentFolder) {
                folderId = currentFolder.folderId
              } else if (isShowingAllNotes) {
                folderId = 'all' // Special value for all notes except sensitive
              }
            }
            
            // ENHANCED: Double-check for sensitive folders - only block in local mode
            if (folderId && folders.find(f => f.folderId === folderId)?.folderType === 'SENSITIVE' && isLocalMode) {
              console.log('🚫 NotesContext: BLOCKING sync for sensitive folder ID in local mode:', folderId)
              return
            }
            
            console.log('🔍 NotesContext: Loading frontend cached notes with folderId:', folderId)
            const frontendCached = await frontendSync.getCachedNotes(folderId)
            cachedNotes = Array.isArray(frontendCached) ? frontendCached : []
          } catch (e) {
            console.warn('⚠️ NotesContext: Failed to load frontend cached notes, falling back to cloud cache', e)
            cachedNotes = await getCachedCloudNotes()
          }
        } else {
          cachedNotes = await getCachedCloudNotes()
        }
        if (cachedNotes.length > 0) {
          console.log(`✅ NotesContext: Showing ${cachedNotes.length} cached notes immediately`)
          updateStoredNotes(cachedNotes) // Will filter by folder if needed
          // Set basic pagination for cached notes
          setPagination(prev => ({
            ...prev,
            totalElements: (currentFolder && !isShowingAllNotes)
              ? cachedNotes.filter(n => (n.folderId || null) === currentFolder.folderId).length
              : cachedNotes.length,
            totalPages: Math.ceil(((currentFolder && !isShowingAllNotes)
              ? cachedNotes.filter(n => (n.folderId || null) === currentFolder.folderId).length
              : cachedNotes.length) / prev.size),
            ...params
          }))
          // Show loading is false for cached notes, but we'll load fresh ones in background
          setLoading(false)
        }

        // For fresh devices: Use progressive loading strategy
        const isInitialSync = cachedNotes.length === 0
        if (isInitialSync) {
          // ENHANCED: Block initial sync for sensitive folders only in local mode
          if (currentFolder?.folderType === 'SENSITIVE' && isLocalMode) {
            console.log('🚫 NotesContext: BLOCKING initial sync for sensitive folder - local mode active')
            return
          }
          
          console.log('🆕 NotesContext: Fresh device detected, starting optimized initial sync...')
          await performProgressiveInitialSync(params)
          return
        }

        // Frontend-only sync mode: Skip backend operations
        if (shouldUseFrontendSync('read')) {
          console.log('🚀 NotesContext: Frontend-only sync enabled - skipping backend sync operations')
          console.log('📱 NotesContext: All sync operations handled by frontend sync service')
          return
        }

        // Legacy backend sync (only if frontend sync is disabled)
        console.log('🔄 NotesContext: Frontend sync disabled, using legacy backend sync...')
        
        // Check for offline notes that need to be uploaded
        await uploadOfflineNotes(cachedNotes)
        
        // Check for notes deleted on other devices and remove them
        await syncDeletedNotes()
        
        // Check for notes updated on other devices and refresh them
        await syncUpdatedNotes()
        
        // Smart background sync: Only sync if cache is stale or forced
        const shouldBackgroundSync = await shouldPerformBackgroundSync(cachedNotes)
        if (shouldBackgroundSync) {
          console.log('🔄 NotesContext: Loading fresh notes from Google Drive in background...')
          await loadFreshNotesFromCloud(params)
        } else {
          console.log('⚡ NotesContext: Skipping background sync - cache is fresh')
        }
      }

    } catch (error) {
      console.error('❌ NotesContext: Error in performSync:', error)
      toast.error('Failed to load notes')
      throw error // Re-throw so fetchNotes can handle it
    }
  }

  /**
   * Optimized initial sync for fresh devices with progressive loading
   */
  const performProgressiveInitialSync = async (params = {}) => {
    try {
      // ENHANCED: Block progressive sync for sensitive folders only in local mode
      if (currentFolder?.folderType === 'SENSITIVE' && isLocalMode) {
        console.log('🚫 NotesContext: BLOCKING progressive sync for sensitive folder - local mode active')
        setLoading(false)
        return
      }
      
      console.log('🚀 NotesContext: Starting progressive initial sync for fresh device')
      
      // Phase 1: Show loading state with progress indication
      setLoading(true)
      toast('Connecting to Google Drive...', { 
        icon: '🔄', 
        duration: 2000,
        id: 'initial-sync-progress'
      })

      // Phase 2: Start with minimal metadata sync (fast)
      try {
        console.log('📊 NotesContext: Phase 1 - Loading note metadata...')
        
        let metadataResponse
        
        // Use frontend sync if available and initialized
        if (shouldUseFrontendSync('read') && frontendSync.isInitialized) {
          console.log('🌐 NotesContext: Using frontend sync for metadata loading')
          const frontendResponse = await frontendSync.loadNotes({
            folderId: params.folderId,
            pageSize: 20,
            sortBy: params.sortBy || 'lastModified',
            sortDir: params.sortDir || 'desc'
          })
          
          // Convert to expected backend format
          metadataResponse = {
            notes: frontendResponse.notes || [],
            totalPages: 1,
            totalElements: frontendResponse.notes?.length || 0,
            currentPage: 0,
            pageSize: frontendResponse.notes?.length || 0
          }
          
          console.log(`✅ NotesContext: Frontend sync loaded ${metadataResponse.notes.length} notes for metadata`)
        } else {
          console.log('🔄 NotesContext: Using backend API for metadata loading')
          metadataResponse = await notesAPI.getNotes({ 
            ...params, 
            page: 0, 
            size: 20, // Start with just 20 notes
            metadataOnly: true // Request metadata only if backend supports it
          })
        }
        
        if (metadataResponse?.notes?.length > 0) {
          console.log(`✅ NotesContext: Got ${metadataResponse.notes.length} note previews`)
          
          // Show note previews immediately (titles only)
          const previewNotes = metadataResponse.notes.map(note => ({
            ...note,
            isPreview: true, // Mark as preview
            content: note.content ? note.content.substring(0, 100) + '...' : '' // Truncated content
          }))
          
          updateStoredNotes(previewNotes)
          setLoading(false)
          
          // Update pagination
          setPagination(prev => ({
            ...prev,
            page: metadataResponse.currentPage || 0,
            totalPages: metadataResponse.totalPages || 1,
            totalElements: metadataResponse.totalElements || previewNotes.length,
            size: metadataResponse.pageSize || 20
          }))
          
          toast.success(`Found ${metadataResponse.totalElements || previewNotes.length} notes`, { 
            duration: 2000,
            id: 'initial-sync-progress'
          })

          // Phase 3: Load full content in background batches
          await loadFullContentInBatches(metadataResponse.totalElements || previewNotes.length, params)
        } else {
          // No notes found
          console.log('📝 NotesContext: No notes found on fresh device')
          setLoading(false)
          toast('No notes found. You can start creating notes!', { 
            icon: '📝',
            duration: 3000
          })
        }
        
             } catch (metadataError) {
         console.warn('⚠️ NotesContext: Metadata sync failed, trying fallback strategies')
         // Use comprehensive fallback strategies
         const fallbackSuccess = await retryInitialSyncWithFallback(params)
         if (!fallbackSuccess) {
           // Last resort: try standard sync with very small page size
           try {
             await loadFreshNotesFromCloud({ ...params, size: 5 })
           } catch (standardSyncError) {
             console.error('❌ NotesContext: All sync strategies failed')
             throw standardSyncError
           }
         }
       }

    } catch (error) {
      console.error('❌ NotesContext: Progressive initial sync failed:', error)
      setLoading(false)
      
      // Show user-friendly error with retry option
      toast.error('Initial sync failed. Click here to retry.', {
        duration: 8000,
        id: 'initial-sync-error',
        action: {
          label: 'Retry',
          onClick: () => performProgressiveInitialSync(params)
        }
      })
      
      // Track failed sync
      localStorage.setItem('lastFailedSync', Date.now().toString())
    }
  }

  /**
   * Load full note content in background batches for better performance
   */
  const loadFullContentInBatches = async (totalNotes, params) => {
    try {
      console.log(`🔄 NotesContext: Loading full content for ${totalNotes} notes in batches...`)
      
      const batchSize = 10 // Load 10 notes at a time
      const totalBatches = Math.ceil(totalNotes / batchSize)
      let loadedCount = 0

      // Show progress indicator
      toast('Loading note content...', { 
        icon: '📥', 
        duration: 3000,
        id: 'batch-loading'
      })

      for (let batch = 0; batch < totalBatches; batch++) {
        try {
          console.log(`📦 NotesContext: Loading batch ${batch + 1}/${totalBatches}`)
          
          const batchResponse = await notesAPI.getNotes({
            ...params,
            page: batch,
            size: batchSize
          })

          if (batchResponse?.notes?.length > 0) {
            // Cache this batch
            await cacheNotesInIndexedDB(batchResponse.notes)
            
            // Update UI with full content notes
            setNotes(prev => {
              const merged = [...prev, ...batchResponse.notes]
              const byId = new Map()
              for (const n of merged) {
                const key = n.noteId || n.id
                if (!key) continue
                const existing = byId.get(key)
                if (!existing) {
                  byId.set(key, n)
                } else {
                  const a = new Date(existing.lastModified || existing.modifiedTime || existing.updatedAt || existing.createdAt || 0).getTime()
                  const b = new Date(n.lastModified || n.modifiedTime || n.updatedAt || n.createdAt || 0).getTime()
                  if (b > a) {
                    byId.set(key, n)
                  } else if (b === a) {
                    if ((n.folderId != null && existing.folderId == null)) byId.set(key, n)
                  }
                }
              }
              return Array.from(byId.values())
            })

            loadedCount += batchResponse.notes.length
            console.log(`✅ NotesContext: Loaded ${loadedCount}/${totalNotes} notes`)

            // Add small delay between batches to prevent overwhelming the server
            if (batch < totalBatches - 1) {
              await new Promise(resolve => setTimeout(resolve, 200))
            }
          }

        } catch (batchError) {
          console.warn(`⚠️ NotesContext: Batch ${batch + 1} failed:`, batchError)
          // Continue with next batch instead of failing completely
        }
      }

      console.log(`🎉 NotesContext: Completed initial sync - loaded ${loadedCount} notes`)
      toast.success(`All ${loadedCount} notes loaded!`, { 
        duration: 2000,
        id: 'batch-loading'
      })

      // Mark initial sync as completed
      localStorage.setItem('lastSyncTime', Date.now().toString())
      localStorage.setItem('initialSyncCompleted', 'true')

    } catch (error) {
      console.error('❌ NotesContext: Batch loading failed:', error)
      toast.error('Some notes may not have loaded completely', { duration: 3000 })
    }
  }

  /**
   * Retry initial sync with fallback strategies
   */
  const retryInitialSyncWithFallback = async (params = {}) => {
    try {
      console.log('🔄 NotesContext: Retrying initial sync with fallback strategies...')
      
      // Strategy 1: Try with very small batch size
      try {
        console.log('🔄 NotesContext: Trying minimal batch strategy...')
        const minimalResponse = await notesAPI.getNotes({ 
          ...params, 
          page: 0, 
          size: 5 // Very small batch
        })
        
        if (minimalResponse?.notes?.length > 0) {
          updateStoredNotes(minimalResponse.notes)
          await cacheNotesInIndexedDB(minimalResponse.notes)
          setLoading(false)
          
          toast.success(`Loaded ${minimalResponse.notes.length} notes (limited due to connection)`, {
            duration: 3000
          })
          
          // Continue loading more in background
          if (minimalResponse.totalElements > minimalResponse.notes.length) {
            setTimeout(() => {
              loadRemainingNotesGradually(minimalResponse.totalElements, minimalResponse.notes.length, params)
            }, 2000)
          }
          
          return true
        }
      } catch (minimalError) {
        console.warn('⚠️ NotesContext: Minimal batch strategy failed:', minimalError)
      }
      
      // Strategy 2: Try metadata-only approach
      try {
        console.log('🔄 NotesContext: Trying metadata-only strategy...')
        const metadataResponse = await notesAPI.getNotes({ 
          ...params, 
          page: 0, 
          size: 50,
          metadataOnly: true
        })
        
        if (metadataResponse?.notes?.length > 0) {
          const previewNotes = metadataResponse.notes.map(note => ({
            ...note,
            isPreview: true,
            content: 'Loading content...'
          }))
          
          updateStoredNotes(previewNotes)
          setLoading(false)
          
          toast.success(`Found ${previewNotes.length} notes (loading content...)`, {
            duration: 3000
          })
          
          return true
        }
      } catch (metadataError) {
        console.warn('⚠️ NotesContext: Metadata strategy failed:', metadataError)
      }
      
      // Strategy 3: Offline mode suggestion
      console.log('💾 NotesContext: All strategies failed, suggesting offline mode')
      setLoading(false)
      
      toast.error('Unable to sync with cloud. Would you like to work offline?', {
        duration: 10000,
        action: {
          label: 'Work Offline',
          onClick: () => {
            // Switch to local mode
            window.location.href = '/local-mode'
          }
        }
      })
      
      return false
      
    } catch (error) {
      console.error('❌ NotesContext: All fallback strategies failed:', error)
      setLoading(false)
      return false
    }
  }

  /**
   * Load remaining notes gradually in background
   */
  const loadRemainingNotesGradually = async (totalNotes, loadedCount, params) => {
    try {
      console.log(`🔄 NotesContext: Loading remaining ${totalNotes - loadedCount} notes gradually...`)
      
      const remainingBatches = Math.ceil((totalNotes - loadedCount) / 5)
      let currentLoaded = loadedCount
      
      for (let batch = 0; batch < remainingBatches; batch++) {
        try {
          const page = Math.floor(currentLoaded / 5)
          const response = await notesAPI.getNotes({
            ...params,
            page: page + 1, // Next page
            size: 5
          })
          
          if (response?.notes?.length > 0) {
            // Add to existing notes
            setNotes(prev => {
              const merged = [...prev, ...response.notes]
              const byId = new Map()
              for (const n of merged) {
                const key = n.noteId || n.id
                if (!key) continue
                const existing = byId.get(key)
                if (!existing) {
                  byId.set(key, n)
                } else {
                  const a = new Date(existing.lastModified || existing.modifiedTime || existing.updatedAt || existing.createdAt || 0).getTime()
                  const b = new Date(n.lastModified || n.modifiedTime || n.updatedAt || n.createdAt || 0).getTime()
                  if (b > a) {
                    byId.set(key, n)
                  } else if (b === a) {
                    if ((n.folderId != null && existing.folderId == null)) byId.set(key, n)
                  }
                }
              }
              return Array.from(byId.values())
            })
            await cacheNotesInIndexedDB(response.notes)
            
            currentLoaded += response.notes.length
            console.log(`📦 NotesContext: Gradually loaded ${currentLoaded}/${totalNotes} notes`)
          }
          
          // Wait longer between gradual loads
          await new Promise(resolve => setTimeout(resolve, 1000))
          
        } catch (batchError) {
          console.warn(`⚠️ NotesContext: Gradual batch failed:`, batchError)
          break // Stop on error
        }
      }
      
      if (currentLoaded >= totalNotes) {
        toast.success('All notes loaded successfully!', { duration: 2000 })
        localStorage.setItem('lastSyncTime', Date.now().toString())
        localStorage.setItem('initialSyncCompleted', 'true')
      }
      
    } catch (error) {
      console.error('❌ NotesContext: Gradual loading failed:', error)
    }
  }

  /**
   * Check if an error indicates Google Drive token expiration or authentication issues
   */
  const isGoogleDriveTokenExpiredError = (error) => {
    // Check for specific error messages from backend
    const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || ''
    const responseData = error.response?.data || {}
    
    // Common Google Drive authentication failure patterns
    const authFailurePatterns = [
      'Google Drive token expired',
      'please reconnect your account',
      'Failed to list files: Google Drive token expired',
      'No cloud provider configured',
      'Invalid Google Drive credentials',
      'TOKEN_EXPIRED',
      'INVALID_CREDENTIALS',
      'NOT_AUTHENTICATED',
      'Google Drive not configured',
      'Authentication failed',
      'access token',
      'refresh token'
    ]
    
    // Check if any pattern matches
    const hasAuthFailure = authFailurePatterns.some(pattern => 
      errorMessage.toLowerCase().includes(pattern.toLowerCase())
    )
    
    // Also check for 401 unauthorized errors which often indicate auth issues
    const isUnauthorized = error.response?.status === 401
    
    // Check for specific cloud storage error codes
    const hasCloudError = responseData.errorCode === 'TOKEN_EXPIRED' || 
                         responseData.errorCode === 'INVALID_CREDENTIALS' ||
                         responseData.errorCode === 'NOT_AUTHENTICATED'
    
    // Check for server restart scenarios where cloud provider state is lost
    const isServerRestartAuth = errorMessage.includes('No cloud provider configured') ||
                               errorMessage.includes('cloud provider not found')
    
    return hasAuthFailure || isUnauthorized || hasCloudError || isServerRestartAuth
  }

  /**
   * Check if a successful response contains a Google Drive token expiration error
   */
  const isSuccessResponseWithGoogleDriveError = (response) => {
    // Backend returns successful response but with error field when Google Drive fails
    const errorMessage = response?.error || ''
    return errorMessage.includes('Failed to get notes from cloud') &&
           (errorMessage.includes('Google Drive token expired') || 
            errorMessage.includes('please reconnect your account') ||
            errorMessage.includes('Failed to list files: Google Drive token expired'))
  }

  /**
   * Hide the reconnection banner
   */
  const hideReconnectionBanner = () => {
    setShowReconnectionBanner(false)
  }

  /**
   * Load fresh notes from Google Drive and update the UI
   */
  const loadFreshNotesFromCloud = async (params = {}) => {
    // Prevent multiple simultaneous requests
    if (loadFreshNotesFromCloud.isLoading) {
      console.log('⏳ NotesContext: Already loading fresh notes, skipping duplicate request')
      return
    }
    
    try {
      loadFreshNotesFromCloud.isLoading = true
      
      // Ensure folderId is included in parameters if not already provided
      let folderId = params.folderId
      if (folderId === undefined) {
        // Determine folderId based on current context
        if (!isShowingAllNotes && currentFolder) {
          folderId = currentFolder.folderId
        } else if (isShowingAllNotes) {
          folderId = 'all' // Special value for all notes except sensitive
        }
      }
      
      const mergedParams = { 
        ...pagination, 
        ...params,
        ...(folderId !== undefined && { folderId })
      }
      console.log('🔍 NotesContext: Fetching fresh notes from Google Drive with params:', mergedParams)

      // Check if we're fetching from a sensitive folder
      const isSensitiveFolder = currentFolder?.folderType === 'SENSITIVE'
      
      let response
      
      if (isSensitiveFolder) {
        // Use frontend encryption service for sensitive notes
        console.log('🔐 NotesContext: Fetching sensitive notes from local storage')
        
        if (!sensitiveSession.isAuthenticated || !sensitiveSession.sessionPassword) {
          console.error('🔐 NotesContext: Not authenticated for sensitive folder')
          loadFreshNotesFromCloud.isLoading = false
          return
        }
        
        // Fetch decrypted notes from local storage
        try {
          const sensitiveNotes = await sensitiveNotesService.getNotes(
            currentFolder.folderId,
            sensitiveSession.sessionPassword
          )
          
          // Convert to expected response format
          response = sensitiveNotes
        } catch (error) {
          console.error('🔐 NotesContext: Failed to fetch sensitive notes:', error)
          if (error.message.includes('Decryption failed')) {
            console.log('🔐 NotesContext: Decryption failed, revoking session')
            sensitiveSession.revoke(false, 'decryption_failed')
            toast.error('🔒 Failed to decrypt notes. Please re-authenticate.', {
              duration: 4000
            })
            return
          }
          throw error // Re-throw other errors
        }
        
        // Convert to expected format (local service returns array directly)
        if (Array.isArray(response)) {
          response = {
            notes: response,
            totalPages: 1,
            totalElements: response.length,
            currentPage: 0,
            pageSize: response.length
          }
        }
      } else {
        // Use frontend sync for reads if enabled, otherwise fallback to backend
        try {
          const useFrontend = shouldUseFrontendSync('read')
          console.log('🔍 NotesContext: Sync decision for reading notes:')
          console.log('  - shouldUseFrontendSync("read"):', useFrontend)
          console.log('  - frontendSync.isInitialized:', frontendSync.isInitialized)
          
          if (useFrontend) {
            console.log('🌐 NotesContext: Using frontend-only sync for reading notes')
            
            // Check if frontend sync is actually initialized
            if (!frontendSync.isInitialized) {
              console.log('⏳ NotesContext: Frontend sync not ready yet, waiting for initialization...')
              
              // Wait for frontend sync to initialize (max 5 seconds)
              let waitTime = 0
              const maxWaitTime = 5000
              const checkInterval = 100
              
              while (!frontendSync.isInitialized && waitTime < maxWaitTime) {
                await new Promise(resolve => setTimeout(resolve, checkInterval))
                waitTime += checkInterval
                if (waitTime % 1000 === 0) {
                  console.log(`⏳ NotesContext: Still waiting for frontend sync... (${waitTime/1000}s)`)
                }
              }
              
              if (!frontendSync.isInitialized) {
                console.warn('⚠️ NotesContext: Frontend sync not ready; skipping backend fallback to keep cloud mode frontend-only')
                // Keep using whatever cached notes are present; schedule non-blocking frontend refresh
                setTimeout(async () => {
                  try {
                    const frontendResponse = await frontendSync.loadNotes({
                      folderId: mergedParams.folderId,
                      pageSize: mergedParams.size || 50,
                      sortBy: mergedParams.sortBy || 'lastModified',
                      sortDir: mergedParams.sortDir || 'desc'
                    })
                    updateStoredNotes(frontendResponse.notes || [])
                  } catch (_) {}
                }, 0)
                response = { notes: [], totalPages: 1, totalElements: 0, currentPage: 0, pageSize: 0 }
              } else {
                console.log('✅ NotesContext: Frontend sync now ready! Using frontend sync for reading')
                const frontendResponse = await frontendSync.loadNotes({
                  folderId: mergedParams.folderId,
                  pageSize: mergedParams.size || 50,
                  sortBy: mergedParams.sortBy || 'lastModified',
                  sortDir: mergedParams.sortDir || 'desc'
                })
                
                // Convert to expected backend format
                response = {
                  notes: frontendResponse.notes || [],
                  totalPages: 1, // Frontend loads all at once for now
                  totalElements: frontendResponse.notes?.length || 0,
                  currentPage: 0,
                  pageSize: frontendResponse.notes?.length || 0
                }
                
                console.log(`✅ NotesContext: Frontend sync loaded ${response.notes.length} notes after waiting`)
              }
            } else {
              const frontendResponse = await frontendSync.loadNotes({
                folderId: mergedParams.folderId,
                pageSize: mergedParams.size || 50,
                sortBy: mergedParams.sortBy || 'lastModified',
                sortDir: mergedParams.sortDir || 'desc'
              })
              
              // Convert to expected backend format
              response = {
                notes: frontendResponse.notes || [],
                totalPages: 1, // Frontend loads all at once for now
                totalElements: frontendResponse.notes?.length || 0,
                currentPage: 0,
                pageSize: frontendResponse.notes?.length || 0
              }
              
              console.log(`✅ NotesContext: Frontend sync loaded ${response.notes.length} notes`)
            }
          } else {
            console.log('🔄 NotesContext: Using backend sync for reading notes')
            if (shouldUseFrontendSync('read')) {
              console.log('🚫 NotesContext: Frontend-only mode active, skipping backend read entirely')
              response = { notes: [], totalPages: 1, totalElements: 0, currentPage: 0, pageSize: 0 }
            } else {
              response = await notesAPI.getNotes(mergedParams)
            }
          }
        } catch (frontendError) {
          if (shouldUseFrontendSync('read')) {
            console.error('❌ NotesContext: Frontend sync failed for reading:', frontendError)
            
            // Check if fallback is enabled
            if (SYNC_CONFIG.ENABLE_FALLBACK) {
              console.warn('⚠️ NotesContext: Fallback enabled, trying backend...')
              try {
                response = await notesAPI.getNotes(mergedParams)
                console.log('✅ NotesContext: Backend fallback successful for reading notes')
              } catch (backendError) {
                // Check if this is a Google Drive token expiration error
                if (isGoogleDriveTokenExpiredError(backendError)) {
                  console.log('🔄 NotesContext: Detected Google Drive token expiration during notes fetch')
                  handleGoogleDriveTokenExpiration()
                  loadFreshNotesFromCloud.isLoading = false
                  return // Don't throw - we've handled the error gracefully
                }
                throw backendError // Re-throw other errors
              }
            } else {
              console.log('🚫 NotesContext: Fallback disabled - frontend-only mode')
              // Keep current cached notes; do not throw to avoid triggering backend
              response = { notes: [], totalPages: 1, totalElements: 0, currentPage: 0, pageSize: 0 }
            }
          } else {
            // Check if this is a Google Drive token expiration error
            if (isGoogleDriveTokenExpiredError(frontendError)) {
              console.log('🔄 NotesContext: Detected Google Drive token expiration during notes fetch')
              handleGoogleDriveTokenExpiration()
              loadFreshNotesFromCloud.isLoading = false
              return // Don't throw - we've handled the error gracefully
            }
            throw frontendError // Re-throw other errors
          }
        }
      }
      
      console.log('✅ NotesContext: Fresh notes loaded from Google Drive:', response)
      
      if (response) {
        // Check if successful response contains Google Drive token expiration error
        if (isSuccessResponseWithGoogleDriveError(response)) {
          console.log('🔄 NotesContext: Detected Google Drive token expiration in successful response')
          handleGoogleDriveTokenExpiration()
          return // Don't process notes, we've handled the error gracefully
        }
        
        // Extract notes array from response
        const freshNotesArray = response.notes || response.content || []
        console.log(`📝 NotesContext: Updating UI with ${freshNotesArray.length} fresh notes from Google Drive`)
        
        // Only update UI with fresh notes if we actually got some, otherwise keep cached notes
        if (freshNotesArray.length > 0) {
          console.log('✅ NotesContext: Fresh notes found, merging with offline notes')
          
          // 📈 Smart Cache Update: Merge server notes with offline notes
          await cacheNotesInIndexedDB(freshNotesArray)
          
          // Get the merged notes (server + offline) to display in UI
          const mergedNotes = await getCachedCloudNotes()
          updateStoredNotes(mergedNotes) // Use merged notes including offline-created ones
          
          console.log('💾 NotesContext: Fresh notes merged with offline notes and cached')
          console.log('🎉 NotesContext: Fresh notes from Google Drive loaded and merged with offline notes!')
        } else {
          console.log('📱 NotesContext: No fresh notes from Google Drive, keeping cached notes visible')
          // Don't update notes state - keep showing cached notes
        }
        
        // Update pagination with fresh data (even if notes are empty for accurate counts)
        setPagination(prev => ({
          ...prev,
          page: response.currentPage || response.page || prev.page,
          totalPages: response.totalPages || prev.totalPages,
          totalElements: response.totalElements || prev.totalElements,
          size: response.pageSize || response.size || prev.size,
          ...params
        }))
      } else {
        console.log('⚠️ NotesContext: No fresh notes data from Google Drive')
      }
    } catch (error) {
      console.error('❌ NotesContext: Error loading fresh notes from Google Drive:', error)
      
      // Track failed sync attempts
      localStorage.setItem('lastFailedSync', Date.now().toString())
      
      // Check if this is a Google Drive token expiration error
      if (isGoogleDriveTokenExpiredError(error)) {
        console.log('🔄 NotesContext: Detected Google Drive token expiration in catch block')
        handleGoogleDriveTokenExpiration()
        return // Don't show additional error messages
      }
      
      // Handle timeout errors specifically for fresh sessions
      if (error.code === 'ECONNABORTED' && notes.length === 0) {
        console.log('⏳ NotesContext: Initial sync timeout for fresh session')
        toast.error('Initial sync is taking longer than expected. The app will retry automatically in a few minutes.', {
          duration: 8000,
          id: 'sync-timeout'
        })
      }
      // Don't show error toast if we already have cached notes showing
      else if (notes.length === 0) {
        toast.error('Failed to load notes from Google Drive')
      } else {
        console.log('📱 NotesContext: Failed to load fresh notes, but cached notes are still showing')
        toast('Using cached notes (sync failed)', {icon: 'ℹ️'})
      }
    } finally {
      // Always clear the loading flag
      loadFreshNotesFromCloud.isLoading = false
    }
  }

  /**
   * Process single-purpose note data before saving
   */
  const processSinglePurposeNote = (noteData) => {
    const noteType = detectNoteType(noteData)
    
    // Validate note type data
    if (!validateNoteTypeData(noteData)) {
      console.warn(`⚠️ NotesContext: Invalid data for note type: ${noteType}`)
    }
    
    // Special processing for flowchart notes
    if (noteType === 'FLOWCHART' && noteData.flowchartData) {
      const validation = validateFlowchartData(noteData.flowchartData)
      if (!validation.isValid) {
        throw new Error(`Invalid flowchart data: ${validation.errors.join(', ')}`)
      }
    }
    
    // Ensure single-purpose notes have empty content
    if (noteType !== 'STANDARD') {
      return {
        ...noteData,
        content: '' // Single-purpose notes have empty content
      }
    }
    
    return noteData
  }

  /**
   * Create a new note with optimistic UI updates
   */
  const createNote = async (noteData, password = null) => {
    try {
      console.log('🔍 NotesContext: Creating note with optimistic update:', noteData)
      
      // Process single-purpose note data
      const processedNoteData = processSinglePurposeNote(noteData)
      
      // If in local mode, use local note creation (already fast)
      if (isLocalMode) {
        setLoading(true)
        const newNote = await createLocalNote(processedNoteData)
        setLoading(false)
        // Local notes are already managed by LocalModeContext, no need to update state here
        return newNote
      }

      // Check if this is a sensitive note (being created in a sensitive folder)
      // Only check the actual folderId being used, not the current folder context
      const isSensitiveNote = processedNoteData.folderId && folders.find(f => f.folderId === processedNoteData.folderId)?.folderType === 'SENSITIVE'
      
      console.log('🔍 NotesContext: Sensitive note check:', {
        noteDataFolderId: processedNoteData.folderId,
        folders: folders.map(f => ({ id: f.folderId, type: f.folderType, name: f.name })),
        isSensitiveNote,
        currentFolder: currentFolder?.name,
        currentFolderType: currentFolder?.folderType
      })
      
      if (isSensitiveNote) {
        console.log('🔐 NotesContext: Creating sensitive note - password required')
        throw new Error('Password required for sensitive note creation. Please authenticate first.')
      }
      
      // ⚡ OPTIMISTIC UPDATE: Create temporary note and show immediately in UI (non-sensitive notes only)
      const optimisticNote = {
        ...processedNoteData,
        noteId: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        createdAt: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        isOptimistic: true, // Flag to identify optimistic notes
        uploading: true // Show uploading indicator
      }
      
      // 🚀 Instantly add to UI - no waiting!
      setNotes(prev => [optimisticNote, ...prev])
      
      // 💾 Cache optimistic note immediately for instant access
      try {
        cacheSingleNote(optimisticNote)
      } catch (_) {}
      console.log('⚡ NotesContext: Optimistic note added to UI instantly')
      
      // 📤 Background upload (non-blocking) - only for non-sensitive notes
      uploadNoteToServer(optimisticNote, processedNoteData, password)
      
      // ✅ Return immediately - UI is already updated!
      toast.success('Note created! Uploading in background...')
      return optimisticNote
      
    } catch (error) {
      console.error('❌ NotesContext: Error in optimistic note creation:', error)
      toast.error('Failed to create note')
      throw error
    }
  }

  /**
   * Upload note to server in background (non-blocking)
   */
  const uploadNoteToServer = async (optimisticNote, noteData, password = null) => {
    try {
      console.log('📤 NotesContext: Starting background upload for note:', optimisticNote.title)
      
      // Skip upload for sensitive notes only in local mode
      const isSensitiveNote = optimisticNote.isSensitive || 
                             (optimisticNote.folderId && folders.find(f => f.folderId === optimisticNote.folderId)?.folderType === 'SENSITIVE')
      
      if (isSensitiveNote && isLocalMode) {
        console.log('🔐 NotesContext: Skipping server upload for sensitive note - local mode active')
        return
      }
      
      if (isSensitiveNote && !isLocalMode) {
        console.log('🔐 NotesContext: Uploading sensitive note to cloud storage')
      }
      
      // Use frontend sync if enabled, otherwise fallback to backend
      let serverNote
      if (shouldUseFrontendSync('create')) {
        try {
          console.log('🌐 NotesContext: Using frontend-only sync for create')
          const noteToSave = { ...optimisticNote, ...noteData }
          console.log('📝 NotesContext: Note data being saved to frontend sync:', noteToSave)
          console.log('📝 NotesContext: Note ID for save:', noteToSave.noteId)
          console.log('📝 NotesContext: Note title for save:', noteToSave.title)
          
          const response = await frontendSync.saveNote(noteToSave)
          serverNote = response.note || response // Handle different response formats
        } catch (frontendError) {
          console.error('❌ NotesContext: Frontend sync failed:', frontendError)
          
          // Check if fallback is enabled
          if (SYNC_CONFIG.ENABLE_FALLBACK) {
            console.warn('⚠️ NotesContext: Fallback enabled, trying backend...')
            serverNote = await notesAPI.createNote(noteData, password)
          } else {
            console.log('🚫 NotesContext: Fallback disabled - frontend-only mode')
            throw frontendError
          }
        }
      } else {
        console.log('🔄 NotesContext: Using backend sync for create')
        serverNote = await notesAPI.createNote(noteData, password)
      }
      
      if (serverNote && serverNote.noteId) {
        console.log('✅ NotesContext: Background upload successful, updating with server data')
        
        // 🔄 Replace optimistic note with real server note
        setNotes(prev => prev.map(note => 
          note.noteId === optimisticNote.noteId 
            ? { ...serverNote, justUploaded: true } // Mark as just uploaded
            : note
        ))
        
        // 💾 Update cache with server note
        await updateOptimisticNoteWithServerData(optimisticNote.noteId, serverNote)
        
        // Mark note creation time to prevent unnecessary background sync
        localStorage.setItem('lastNoteCreation', Date.now().toString())
        
        toast.success('📤 Note uploaded successfully!', { duration: 2000 })
        
      } else {
        throw new Error('Invalid response from server')
      }
      
    } catch (error) {
      console.error('❌ NotesContext: Background upload failed:', error)
      
      // Check if this is a Google Drive token expiration error
      if (isGoogleDriveTokenExpiredError(error)) {
        console.log('🔄 NotesContext: Google Drive token expired during background upload')
        handleGoogleDriveTokenExpiration()
        await markOptimisticNoteAsFailed(optimisticNote.noteId, 'Google Drive connection expired')
        return
      }
      
      // Check if this is a network error - convert to offline note
      if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || !navigator.onLine) {
        console.log('📡 NotesContext: Network error during background upload, converting to offline note')
        
        // Convert optimistic note to offline note
        const offlineNote = {
          ...optimisticNote,
          noteId: `offline-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          id: `offline-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          isOptimistic: false,
          uploading: false,
          isOfflineCreated: true,
          pendingSync: true
        }
        
        // Update UI and cache
        setNotes(prev => prev.map(note => 
          note.noteId === optimisticNote.noteId ? offlineNote : note
        ))
        await updateOptimisticNoteWithServerData(optimisticNote.noteId, offlineNote)
        
        toast.success('📱 Note saved offline - will sync when connection restored')
        return
      }
      
      // Other errors - mark as failed but keep in UI
      await markOptimisticNoteAsFailed(optimisticNote.noteId, error.message)
      toast.error('❌ Upload failed - note saved locally')
    }
  }

  /**
   * Update optimistic note with server data after successful upload
   */
  const updateOptimisticNoteWithServerData = async (optimisticNoteId, serverNote) => {
    try {
      const db = await openCloudCacheDB()
      const transaction = db.transaction(['cloud_notes'], 'readwrite')
      const store = transaction.objectStore('cloud_notes')
      
      // Remove the optimistic note
      await store.delete(optimisticNoteId)
      
      // Add the server/offline note
      const cacheNote = {
        ...serverNote,
        cachedAt: new Date().toISOString(),
        cacheType: 'cloud'
      }
      await store.put(cacheNote)
      
      console.log(`💾 NotesContext: Replaced optimistic note ${optimisticNoteId} with final note`)
    } catch (error) {
      console.warn('⚠️ NotesContext: Failed to update cached note after upload:', error)
    }
  }

  /**
   * Mark optimistic note as upload failed
   */
  const markOptimisticNoteAsFailed = async (optimisticNoteId, errorMessage) => {
    try {
      // Update UI to show failed state
      setNotes(prev => prev.map(note => 
        note.noteId === optimisticNoteId 
          ? { 
              ...note, 
              uploading: false, 
              uploadFailed: true, 
              uploadError: errorMessage,
              isOfflineCreated: true, // Convert to offline for retry
              pendingSync: true 
            }
          : note
      ))
      
      // Update cache
      const db = await openCloudCacheDB()
      const transaction = db.transaction(['cloud_notes'], 'readwrite')
      const store = transaction.objectStore('cloud_notes')
      
      const note = await new Promise((resolve, reject) => {
        const request = store.get(optimisticNoteId)
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
      })
      
      if (note) {
        const updatedNote = {
          ...note,
          uploading: false,
          uploadFailed: true,
          uploadError: errorMessage,
          isOfflineCreated: true,
          pendingSync: true,
          lastUploadAttempt: new Date().toISOString()
        }
        await store.put(updatedNote)
        console.log(`⚠️ NotesContext: Marked optimistic note ${optimisticNoteId} as upload failed`)
      }
    } catch (error) {
      console.warn('⚠️ NotesContext: Failed to mark optimistic note as failed:', error)
    }
  }

  /**
   * Update an existing note with optimistic UI updates
   */
  const updateNote = async (noteId, updates) => {
    try {
      console.log('🔍 NotesContext: Updating note with optimistic update:', noteId, updates)
      
      // Resolve temp ID to permanent ID if mapping exists (same logic as EditNotePage)
      let resolvedNoteId = noteId
      try {
        if (noteId && noteId.startsWith('temp-')) {
          const raw = localStorage.getItem('tempToPermMap') || '{}'
          const map = JSON.parse(raw)
          if (map[noteId]) {
            resolvedNoteId = map[noteId]
            console.log('🔄 NotesContext: Resolved temp ID to permanent ID:', resolvedNoteId)
          }
        }
      } catch (_) {}
      
      // If in local mode, use local note update (already fast)
      if (isLocalMode) {
        setLoading(true)
        const updatedNote = await updateLocalNote(resolvedNoteId, updates)
        setLoading(false)
        // Local notes are already managed by LocalModeContext, no need to update state here
        return updatedNote
      }
      
      // Find the original note before updating (use resolved ID)
      let originalNote = notes.find(note => note.noteId === resolvedNoteId)
      
      // If not found with resolved ID, try with original temp ID as fallback
      if (!originalNote && noteId !== resolvedNoteId) {
        originalNote = notes.find(note => note.noteId === noteId)
        console.log('🔍 NotesContext: Found note with original temp ID as fallback')
      }
      
      if (!originalNote) {
        console.warn('⚠️ NotesContext: Note not found in local state:', noteId, '(resolved:', resolvedNoteId, ')')
        throw new Error('Note not found')
      }

      // Check if this is a sensitive note
      const isSensitiveNote = originalNote.isSensitive || 
                             (originalNote.folderId && folders.find(f => f.folderId === originalNote.folderId)?.folderType === 'SENSITIVE')
      
      if (isSensitiveNote) {
        console.log('🔐 NotesContext: Updating sensitive note (password already handled by EditNotePage)')
        // Password authentication and encryption is handled by EditNotePage before calling this function
      }
      
      // 🚀 OPTIMISTIC UPDATE: Update in UI immediately for better UX
      const optimisticNote = {
        ...originalNote,
        ...updates,
        lastModified: new Date().toISOString(),
        isOptimistic: true, // Flag to identify optimistic updates
        updating: true // Show updating indicator
      }
      
      console.log('⚡ NotesContext: Optimistically updating note in UI')
      setNotes(prev => prev.map(note => {
        const currentNoteId = note.noteId
        return currentNoteId === resolvedNoteId || currentNoteId === noteId ? optimisticNote : note
      }))
      
      // 💾 Cache optimistic update immediately for instant access
      try {
        cacheSingleNote(optimisticNote)
      } catch (_) {}
      console.log('💾 NotesContext: Optimistic note update cached in IndexedDB')
      
      // 🔄 BACKGROUND UPDATE: Now perform actual update to server/cloud
      console.log('🌐 NotesContext: Starting background update to cloud...')
      
      // Don't await this - let it happen in background
      performBackgroundUpdate(resolvedNoteId, updates, originalNote, optimisticNote)
      
      // ✅ Return immediately - UI is already updated!
      return optimisticNote
      
    } catch (error) {
      console.error('❌ NotesContext: Error in optimistic note update:', error)
      throw error
    }
  }

  /**
   * Perform the actual update to cloud storage in the background
   */
  const performBackgroundUpdate = async (noteId, updates, originalNote, optimisticNote) => {
    try {
      console.log('🔄 NotesContext: Performing background update for note:', noteId)
      
      // Check if this is a sensitive note
      const isSensitiveNote = optimisticNote?.isEncrypted || updates?.isEncrypted
      
      // Use frontend sync if enabled, otherwise fallback to backend
      let response
      if (shouldUseFrontendSync('update')) {
        try {
          console.log('🌐 NotesContext: Using frontend-only sync for update')
          const frontendResponse = await frontendSync.saveNote({ ...optimisticNote, ...updates })
          response = frontendResponse.note || frontendResponse // Handle different response formats
        } catch (frontendError) {
          console.warn('⚠️ NotesContext: Frontend sync failed, falling back to backend:', frontendError)
          response = await notesAPI.updateNote(noteId, updates)
        }
      } else {
        console.log('🔄 NotesContext: Using backend sync for update')
        response = await notesAPI.updateNote(noteId, updates)
      }
      
      // Check if update was successful - API returns note data directly
      const isSuccessful = response && response.noteId
      
      if (isSuccessful) {
        console.log('✅ NotesContext: Background update successful:', response)
        
        // Replace optimistic note with real server response
        const serverNote = { ...response, isOptimistic: false, updating: false }
        
        // For sensitive notes, also update SensitiveNotesService
        if (isSensitiveNote) {
          console.log('🔐 NotesContext: Updating sensitive note in SensitiveNotesService')
          try {
            const { sensitiveNotesService } = await import('../services/sensitiveNotesService')
            // Get the password from the session
            const password = sensitiveSession?.sessionPassword
            if (password) {
              // The serverNote should already be encrypted by EditNotePage
              // We need to store it directly in SensitiveNotesService without re-encryption
              const existingNotes = sensitiveNotesService.getEncryptedNotes()
              const encryptedNote = {
                ...serverNote,
                isEncrypted: true,
                encryptedAt: new Date().toISOString()
              }
              existingNotes[noteId] = encryptedNote
              sensitiveNotesService.saveEncryptedNotes(existingNotes)
              console.log('✅ NotesContext: Sensitive note stored in SensitiveNotesService')
            } else {
              console.warn('⚠️ NotesContext: No password available for SensitiveNotesService update')
            }
          } catch (sensitiveError) {
            console.warn('⚠️ NotesContext: Failed to update SensitiveNotesService:', sensitiveError)
          }
        }
        
        setNotes(prev => prev.map(note => {
          const currentNoteId = note.noteId
          return currentNoteId === noteId ? serverNote : note
        }))
        
                 // Update cache with real server data
         await updateCachedNoteWithServerData(noteId, serverNote)
         console.log('✅ NotesContext: Note updated successfully in background')
         // Optional: Show success message for cloud sync completion
         // showSuccess('☁️ Note synced to cloud successfully!')
        
      } else {
        console.error('❌ NotesContext: Background update failed:', response)
        // Rollback the optimistic update
        await rollbackOptimisticUpdate(noteId, originalNote)
      }
      
         } catch (error) {
       console.error('❌ NotesContext: Background update error:', error)
       
       // Check if this is a Google Drive token expiration error
       if (isGoogleDriveTokenExpiredError(error)) {
         console.log('🔄 NotesContext: Google Drive token expired during background update')
         handleGoogleDriveTokenExpiration()
         // Rollback and inform user
         await rollbackOptimisticUpdate(noteId, originalNote)
         // Don't show error toast here - the status bar will handle it
       } else if (error.response?.status >= 500 || error.message?.includes('JsonNode')) {
         // Server error or JSON corruption - keep optimistic update but warn user
         console.log('⚠️ NotesContext: Server error during background update, keeping optimistic changes')
         showWarning('⚠️ Cloud sync failed due to corrupted files, but your changes are saved locally.')
         // Don't rollback - the optimistic update is better than the corrupted server state
         // The user's changes are preserved locally
       } else {
         // For other errors, rollback the optimistic update
         await rollbackOptimisticUpdate(noteId, originalNote)
       }
     }
  }

  /**
   * Rollback an optimistic update if the actual update failed
   */
  const rollbackOptimisticUpdate = async (noteId, originalNote) => {
    try {
      console.log('🔄 NotesContext: Rolling back optimistic update for note:', noteId)
      
      // Restore the original note in the UI
      setNotes(prev => prev.map(note => {
        const currentNoteId = note.noteId
        return currentNoteId === noteId ? originalNote : note
      }))
      
      // Restore original note to cache
      await cacheSingleNote(originalNote)
      console.log('💾 NotesContext: Restored original note to cache')
      
      // Don't show toast here - let the EditNotePage handle the status messages
      
    } catch (rollbackError) {
      console.error('❌ NotesContext: Failed to rollback optimistic update:', rollbackError)
      // Don't show toast here - let the EditNotePage handle the status messages
    }
  }

  /**
   * Update cached note with server data after successful background update
   */
  const updateCachedNoteWithServerData = async (noteId, serverNote) => {
    try {
      const db = await openCloudCacheDB()
      const transaction = db.transaction(['cloud_notes'], 'readwrite')
      const store = transaction.objectStore('cloud_notes')
      
      // Update the cached note with server data
      const cacheNote = {
        ...serverNote,
        cachedAt: new Date().toISOString(),
        cacheType: 'cloud'
      }
      await store.put(cacheNote)
      
      console.log(`💾 NotesContext: Updated cached note ${noteId} with server data`)
    } catch (error) {
      console.warn('⚠️ NotesContext: Failed to update cached note after background update:', error)
    }
  }

  /**
   * Delete a note with optimistic UI updates
   */
  const deleteNote = async (noteId) => {
    try {
      console.log('🔍 NotesContext: Deleting note:', noteId)
      
      // Resolve temp ID to permanent ID if mapping exists (same logic as EditNotePage)
      let resolvedNoteId = noteId
      try {
        if (noteId && noteId.startsWith('temp-')) {
          const raw = localStorage.getItem('tempToPermMap') || '{}'
          const map = JSON.parse(raw)
          if (map[noteId]) {
            resolvedNoteId = map[noteId]
            console.log('🔄 NotesContext: Resolved temp ID to permanent ID for deletion:', resolvedNoteId)
          }
        }
      } catch (_) {}
      
      // If in local mode, use local note deletion
      if (isLocalMode) {
        const success = await deleteLocalNote(resolvedNoteId)
        // Local notes are already managed by LocalModeContext, no need to update state here
        return success
      }
      
      // Store the note before deletion for potential rollback (use resolved ID)
      let noteToDelete = notes.find(note => note.noteId === resolvedNoteId)
      
      // If not found with resolved ID, try with original temp ID as fallback
      if (!noteToDelete && noteId !== resolvedNoteId) {
        noteToDelete = notes.find(note => note.noteId === noteId)
        console.log('🔍 NotesContext: Found note with original temp ID for deletion')
      }
      
      if (!noteToDelete) {
        console.warn('⚠️ NotesContext: Note not found in local state:', noteId, '(resolved:', resolvedNoteId, ')')
        toast.error('Note not found')
        return false
      }

      // Check if this is a sensitive note
      const isSensitiveNote = noteToDelete.isSensitive || 
                             (noteToDelete.folderId && folders.find(f => f.folderId === noteToDelete.folderId)?.folderType === 'SENSITIVE')
      
      if (isSensitiveNote) {
        console.log('🔐 NotesContext: Deleting sensitive note')
        
        // For sensitive notes, deletion is allowed without password authentication
        // since the user is already in the sensitive folder and can see the encrypted content
        
        // Remove from UI immediately (this is the most important part)
        setNotes(prev => prev.filter(note => {
          const currentNoteId = note.noteId
          return currentNoteId !== resolvedNoteId && currentNoteId !== noteId
        }))
        
        // Try to delete from SensitiveNotesService, but don't fail if note doesn't exist
        try {
          await sensitiveNotesService.deleteNote(resolvedNoteId)
          console.log('✅ NotesContext: Note deleted from SensitiveNotesService')
        } catch (error) {
          if (error.message === 'Note not found') {
            console.log('⚠️ NotesContext: Note not found in SensitiveNotesService, continuing with UI removal')
          } else {
            console.warn('⚠️ NotesContext: Failed to delete from SensitiveNotesService:', error)
            // Continue with UI removal anyway
          }
        }
        
        // In cloud mode, also delete from Google Drive
        if (!isLocalMode) {
          console.log('🔐 NotesContext: Deleting sensitive note from Google Drive')
          try {
            // Use frontend sync to delete from Google Drive
            if (shouldUseFrontendSync('delete')) {
              await frontendSync.deleteNote(resolvedNoteId, noteToDelete.cloudFileId, noteToDelete)
              console.log('✅ NotesContext: Sensitive note deleted from Google Drive via frontend sync')
            } else {
              // Fallback to backend API
              await notesAPI.deleteNote(resolvedNoteId)
              console.log('✅ NotesContext: Sensitive note deleted from Google Drive via backend API')
            }
          } catch (cloudError) {
            console.warn('⚠️ NotesContext: Failed to delete sensitive note from Google Drive:', cloudError)
            // Continue with local deletion anyway
          }
        }
        
        // Remove from cache (with error handling and timeout)
        try {
          // Use a shorter timeout for cache removal
          const cacheRemovalPromise = removeCachedNote(resolvedNoteId)
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Cache removal timeout')), 2000)
          )
          
          await Promise.race([cacheRemovalPromise, timeoutPromise])
          console.log('✅ NotesContext: Note removed from cache')
        } catch (error) {
          console.warn('⚠️ NotesContext: Failed to remove from cache:', error)
          // If cache removal fails, track the deleted note ID to prevent it from reappearing
          try {
            const deletedNotes = JSON.parse(localStorage.getItem('deletedSensitiveNotes') || '[]')
            if (!deletedNotes.includes(resolvedNoteId)) {
              deletedNotes.push(resolvedNoteId)
              localStorage.setItem('deletedSensitiveNotes', JSON.stringify(deletedNotes))
              console.log('✅ NotesContext: Note ID tracked as deleted to prevent reappearance')
            }
          } catch (trackError) {
            console.warn('⚠️ NotesContext: Failed to track deleted note:', trackError)
          }
        }
        
        console.log('✅ NotesContext: Sensitive note deleted from UI and cloud')
        toast.success('Secure note deleted!')
        return true
      }
      
      // 🚀 OPTIMISTIC UPDATE: Remove from UI immediately for better UX
      console.log('⚡ NotesContext: Optimistically removing note from UI')
      try {
        setNotes(prev => prev.filter(note => {
          const currentNoteId = note.noteId
          return currentNoteId !== resolvedNoteId && currentNoteId !== noteId
        }))
        console.log('✅ NotesContext: setNotes completed successfully')
      } catch (setNotesError) {
        console.error('❌ NotesContext: setNotes failed:', setNotesError)
        throw setNotesError
      }
      
      console.log('🔍 NotesContext: After setNotes, about to proceed with cache removal...')
      
      // Remove from cache immediately as well (non-critical operation)
      console.log('🔍 NotesContext: About to remove note from cache...')
      try {
        console.log('🔍 NotesContext: Calling removeCachedNote...')
        await removeCachedNote(resolvedNoteId)
        console.log('💾 NotesContext: Optimistically removed note from cache')
      } catch (cacheError) {
        console.warn('⚠️ NotesContext: Cache removal failed (non-critical), continuing with deletion:', cacheError.message)
        // Cache removal is non-critical - continue with deletion
      }
      
      console.log('🔍 NotesContext: Cache removal completed, about to show toast...')
      
      // Show immediate feedback to user
      toast.success('Note deleted!', { duration: 2000 })
      
      // 🔄 BACKGROUND DELETION: Now perform actual deletion from server/cloud
      console.log('🌐 NotesContext: Starting background deletion from cloud...')
      console.log('🔍 NotesContext: About to call performBackgroundDeletion with:', {
        noteId,
        resolvedNoteId,
        noteToDelete: !!noteToDelete,
        noteToDeleteKeys: noteToDelete ? Object.keys(noteToDelete) : null
      })
      
      // Don't await this - let it happen in background, but catch any errors
      console.log('🚀 NotesContext: EXECUTING performBackgroundDeletion call now...')
      performBackgroundDeletion(resolvedNoteId, noteToDelete).catch(error => {
        console.error('❌ NotesContext: Background deletion failed silently:', error)
        // Don't rollback here since the note is already removed from UI
        // The user will see the note is gone, which is probably what they want
      })
      console.log('✅ NotesContext: performBackgroundDeletion call completed (non-awaited)')
      
      return true
      
    } catch (error) {
      console.error('❌ NotesContext: Error in optimistic deletion:', error)
      toast.error('Failed to delete note')
      throw error
    }
  }

  /**
   * Perform the actual deletion from cloud storage in the background
   */
  const performBackgroundDeletion = async (noteId, originalNote) => {
    try {
      console.log('🔄 NotesContext: Performing background deletion for note:', noteId)
      console.log('🔍 NotesContext: Note details:', {
        noteId,
        hasCloudFileId: !!originalNote.cloudFileId,
        cloudFileId: originalNote.cloudFileId,
        shouldUseFrontendSync: shouldUseFrontendSync('delete'),
        isLocalMode
      })
      
      // Use frontend sync if enabled, regardless of cloudFileId (it will be resolved automatically)
      let response
      if (shouldUseFrontendSync('delete')) {
        try {
          console.log('🌐 NotesContext: Using frontend-only sync for delete with attachment cleanup')
          console.log('🔍 NotesContext: Calling frontendSync.deleteNote with:', {
            noteId,
            cloudFileId: originalNote.cloudFileId || 'auto-resolve',
            hasContent: !!originalNote.content
          })
          response = await frontendSync.deleteNote(noteId, originalNote.cloudFileId || null, originalNote.content)
          console.log('✅ NotesContext: Frontend sync delete response:', response)
        } catch (frontendError) {
          console.warn('⚠️ NotesContext: Frontend sync failed, falling back to local deletion:', frontendError)
          // If frontend sync fails, just delete locally since we can't rely on backend
          response = { success: true, message: 'Note deleted locally (frontend sync failed)' }
        }
      } else {
        console.log('🔄 NotesContext: Frontend sync disabled for delete operations')
        // Note was never synced to Google Drive, just delete locally
        response = { success: true, message: 'Note deleted locally (was never synced to cloud)' }
      }
      
      // Check if deletion was successful
      console.log('🔍 NotesContext: Final response from deletion:', response)
      const isSuccessful = response.success || (response.message && response.message.includes('deleted successfully'))
      console.log('🔍 NotesContext: Deletion success check:', { isSuccessful, response })
      
      if (isSuccessful) {
        console.log('✅ NotesContext: Background deletion successful:', response)
        // Note is already removed from UI, nothing more to do
      } else {
        console.error('❌ NotesContext: Background deletion failed:', response)
        // Rollback the optimistic update
        await rollbackOptimisticDeletion(noteId, originalNote)
      }
      
      console.log('✅ NotesContext: Background deletion function completed successfully')
      
    } catch (error) {
      console.error('❌ NotesContext: Background deletion error:', error)
      
      // Check if this is a Google Drive token expiration error
      if (isGoogleDriveTokenExpiredError(error)) {
        console.log('🔄 NotesContext: Google Drive token expired during background deletion')
        handleGoogleDriveTokenExpiration()
        // Rollback and inform user
        await rollbackOptimisticDeletion(noteId, originalNote)
        toast.error('Google Drive connection expired. Note has been restored. Please reconnect and try again.')
      } else {
        // For other errors, rollback the optimistic update
        await rollbackOptimisticDeletion(noteId, originalNote)
      }
    }
  }

  /**
   * Rollback an optimistic deletion if the actual deletion failed
   */
  const rollbackOptimisticDeletion = async (noteId, originalNote) => {
    try {
      console.log('🔄 NotesContext: Rolling back optimistic deletion for note:', noteId)
      
      // Restore the note to the UI
      setNotes(prev => {
        // Check if note is already back (avoid duplicates)
        const exists = prev.some(note => note.noteId === noteId)
        if (exists) return prev
        
        // Add it back in its original position or at the beginning
        return [originalNote, ...prev]
      })
      
      // Restore to cache
      await cacheSingleNote(originalNote)
      console.log('💾 NotesContext: Restored note to cache')
      
      toast.error('Failed to delete note from cloud. Note has been restored.')
      
    } catch (rollbackError) {
      console.error('❌ NotesContext: Failed to rollback optimistic deletion:', rollbackError)
      toast.error('Deletion failed and could not restore note. Please refresh the page.')
    }
  }

  /**
   * Search cached notes in IndexedDB (much faster than server search)
   */
  const searchCachedNotes = async (searchTerm, folderId = null) => {
    if (isLocalMode) return null // Use local mode search instead
    
    try {
      let cachedNotes = []
      
      // Use the appropriate cache based on sync configuration
      if (SYNC_CONFIG.USE_FRONTEND_SYNC && frontendSync) {
        // Use frontend sync cache (same as sensitive note filtering)
        console.log('🔍 NotesContext: Using frontend sync cache for search')
        cachedNotes = await frontendSync.getCachedNotes(folderId)
        console.log('💾 NotesContext: Frontend sync cache search debug:', {
        cachedNotesLength: cachedNotes.length,
        allNotesLength: allNotes.length,
        searchTerm: `"${searchTerm}"`,
        folderId,
        notesInState: notes.length
      })
      } else {
        // Use legacy cloud_notes cache
        console.log('🔍 NotesContext: Using legacy cloud_notes cache for search')
        cachedNotes = await getCachedCloudNotes()
        console.log('💾 NotesContext: Legacy cache search debug:', {
          cachedNotesLength: cachedNotes.length,
          allNotesLength: allNotes.length,
          searchTerm: `"${searchTerm}"`,
          folderId,
          notesInState: notes.length
        })
      }
      
      if (cachedNotes.length === 0) {
        console.log('💾 NotesContext: No cached notes available for search, will fall back to server search')
        
        // If we have notes in memory but not in cache, try searching those
        if (allNotes.length > 0) {
          console.log('💾 NotesContext: Using in-memory notes for search instead of cache')
          const lowerSearchTerm = searchTerm.toLowerCase().trim()
          let filteredNotes = allNotes.filter(note => {
            if (!lowerSearchTerm) return true
            const titleMatch = note.title && note.title.toLowerCase().includes(lowerSearchTerm)
            const contentMatch = note.content && note.content.toLowerCase().includes(lowerSearchTerm)
            return titleMatch || contentMatch
          })
          
          // Apply folder filtering
          if (folderId !== null && folderId !== undefined) {
            if (folderId === 'all') {
              filteredNotes = filteredNotes.filter(note => !isNoteSensitive(note))
            } else if (folderId === 'root') {
              filteredNotes = filteredNotes.filter(note => !note.folderId)
            } else {
              filteredNotes = filteredNotes.filter(note => note.folderId === folderId)
            }
          }
          
          // Sort by lastModified
          filteredNotes.sort((a, b) => {
            const dateA = new Date(a.lastModified)
            const dateB = new Date(b.lastModified)
            return dateB - dateA
          })
          
          console.log(`✅ NotesContext: Found ${filteredNotes.length} matches in memory notes`)
          return filteredNotes
        }
        
        return null // Fall back to server search
      }
      
      console.log(`🔍 NotesContext: Searching ${cachedNotes.length} cached notes locally with optimized indices`)
      const lowerSearchTerm = searchTerm.toLowerCase().trim()
      
      // Filter notes by search term
      let filteredNotes = cachedNotes.filter(note => {
        if (!lowerSearchTerm) return true // Show all if no search term
        
        const titleMatch = note.title && note.title.toLowerCase().includes(lowerSearchTerm)
        const contentMatch = note.content && note.content.toLowerCase().includes(lowerSearchTerm)
        
        return titleMatch || contentMatch
      })
      
      // Apply folder filtering if specified - but only if we're NOT using frontend sync
      // (frontend sync already applies folder filtering when getting cached notes)
      if (!SYNC_CONFIG.USE_FRONTEND_SYNC || !frontendSync) {
        // Legacy cache: Apply folder filtering manually
      if (folderId !== null && folderId !== undefined) {
        if (folderId === 'all') {
          // Show all notes except sensitive ones
          filteredNotes = filteredNotes.filter(note => !isNoteSensitive(note))
        } else if (folderId === 'root') {
          // Show notes that don't belong to any folder
          filteredNotes = filteredNotes.filter(note => !note.folderId)
        } else {
            // Show notes from specific folder
          filteredNotes = filteredNotes.filter(note => note.folderId === folderId)
        }
      }
      }
      // If using frontend sync, folder filtering and sensitive note filtering are already applied
      
      // Sort by lastModified (using lastModified index for performance)
      filteredNotes.sort((a, b) => {
        const dateA = new Date(a.lastModified)
        const dateB = new Date(b.lastModified)
        return dateB - dateA
      })
      
      console.log(`✅ NotesContext: Found ${filteredNotes.length} matches in cached notes using ${SYNC_CONFIG.USE_FRONTEND_SYNC ? 'frontend sync' : 'legacy'} cache`)
      return filteredNotes
      
    } catch (error) {
      console.warn('⚠️ NotesContext: Failed to search cached notes:', error)
      return null // Fall back to server search
    }
  }

  /**
   * Search notes
   */
  const searchNotes = async (searchParams) => {
    try {
      setLoading(true)
      console.log('🔍 NotesContext: Searching notes:', searchParams)
      
      // If in local mode, use local search
      if (isLocalMode) {
        const searchTerm = searchParams.query || searchParams.searchTerm || searchParams.term || ''
        const results = await searchLocalNotes(searchTerm)
        setNotes(results)
        setPagination(prev => ({
          ...prev,
          totalElements: results.length,
          totalPages: Math.ceil(results.length / prev.size)
        }))
        return
      }
      
      // For cloud mode: Try IndexedDB search first (much faster!)
      const searchTerm = searchParams.query || searchParams.searchTerm || searchParams.term || ''
      const folderId = searchParams.folderId
      
      const cachedResults = await searchCachedNotes(searchTerm, folderId)
      
      if (cachedResults !== null) {
        // Success! Use cached results (instant search)
        console.log('⚡ NotesContext: Using cached search results (instant)')
        setNotes(cachedResults)
        setPagination(prev => ({
          ...prev,
          totalElements: cachedResults.length,
          totalPages: Math.ceil(cachedResults.length / prev.size),
          currentPage: 0 // Reset to first page for search results
        }))
        
        // Optional: Background refresh of search results from server for data freshness
        // This ensures search results are current while still providing instant UX
        console.log('🔄 NotesContext: Triggering background refresh of search results...')
        setTimeout(async () => {
          try {
            if (shouldUseFrontendSync('read')) return
            const response = await notesAPI.searchNotes(searchParams)
            if (response && response.notes) {
              const serverResults = response.notes
              // Only update if server results are significantly different
              if (Math.abs(serverResults.length - cachedResults.length) > 2) {
                console.log('🔄 NotesContext: Server search results differ significantly, updating...')
                setNotes(serverResults)
                setPagination(prev => ({
                  ...prev,
                  totalElements: response.totalElements || serverResults.length,
                  totalPages: response.totalPages || Math.ceil(serverResults.length / prev.size)
                }))
                // Update cache with fresh results
                await cacheNotesInIndexedDB(serverResults)
              }
            }
          } catch (error) {
            console.log('🔄 NotesContext: Background search refresh failed (using cached results):', error.message)
          }
        }, 100) // Small delay to not block the UI
        
        return
      }
      
      // Fallback: Use server search if no cache available
      if (shouldUseFrontendSync('read')) {
        console.log('🚫 NotesContext: No cache available and frontend-only mode active; skipping backend search')
        setNotes([])
        setPagination(prev => ({ ...prev, totalElements: 0, totalPages: 0, currentPage: 0 }))
        return
      }
      console.log('🌐 NotesContext: No cache available, using server search...')
      
      try {
        const response = await notesAPI.searchNotes(searchParams)
        
        if (response) {
          // Check if successful response contains Google Drive token expiration error
          if (isSuccessResponseWithGoogleDriveError(response)) {
            console.log('🔄 NotesContext: Detected Google Drive token expiration in search response')
            handleGoogleDriveTokenExpiration()
            return // Don't process notes, we've handled the error gracefully
          }
          
          // Extract notes array from search response
          const notesArray = response.notes || response.content || []
          setNotes(notesArray)
          setPagination(prev => ({
            ...prev,
            page: response.currentPage || response.page || 0,
            totalPages: response.totalPages || 0,
            totalElements: response.totalElements || 0,
            size: response.pageSize || response.size || prev.size
          }))
          
          // Cache the search results for next time
          if (notesArray.length > 0) {
            await cacheNotesInIndexedDB(notesArray)
          }
        }
      } catch (error) {
        // Check if this is a Google Drive token expiration error
        if (isGoogleDriveTokenExpiredError(error)) {
          console.log('🔄 NotesContext: Detected Google Drive token expiration during search')
          handleGoogleDriveTokenExpiration()
          return // Don't show additional error
        }
        throw error // Re-throw other errors
      }
    } catch (error) {
      console.error('❌ NotesContext: Error searching notes:', error)
      toast.error('Failed to search notes')
    } finally {
      setLoading(false)
    }
  }

  /**
   * Refresh notes list
   */
  const refreshNotes = () => {
    // Mark as forced refresh to bypass cache staleness check
    localStorage.setItem('lastForcedSync', Date.now().toString())
    fetchNotes({ forceSync: true })
  }

  /**
   * Load encrypted sensitive notes for list display (no decryption)
   */
  const loadEncryptedSensitiveNotes = async () => {
    if (!currentFolder || currentFolder.folderType !== 'SENSITIVE') {
      console.log('⚠️ NotesContext: loadEncryptedSensitiveNotes called for non-sensitive folder')
      return
    }
    
    try {
      console.log('🔄 NotesContext: Loading encrypted sensitive notes for list display')
      
      const encryptedNotes = await sensitiveNotesService.getEncryptedNotesForFolder(
        currentFolder.folderId
      )
      
      console.log(`✅ NotesContext: Loaded ${encryptedNotes.length} encrypted sensitive notes from sensitive service`)
      
      // Check if there are notes in regular storage that should be in sensitive service
      // This can happen if notes were created through a different path
      if (encryptedNotes.length === 0) {
        console.log('🔍 NotesContext: No notes in sensitive service, checking regular storage for migration...')
        try {
          // Try to load from regular storage to see if there are notes that need migration
          const regularNotes = await frontendSync.getCachedNotes(currentFolder.folderId)
          const sensitiveNotesInRegular = regularNotes.filter(note => 
            note.folderId === currentFolder.folderId && note.isEncrypted
          )
          
          if (sensitiveNotesInRegular.length > 0) {
            console.log(`🔍 NotesContext: Found ${sensitiveNotesInRegular.length} encrypted notes in regular storage that need migration`)
            
            // Filter out notes that were deleted but couldn't be removed from cache
            const deletedNotes = JSON.parse(localStorage.getItem('deletedSensitiveNotes') || '[]')
            const validNotes = sensitiveNotesInRegular.filter(note => !deletedNotes.includes(note.noteId))
            
            // Clean up deleted notes list if it gets too large (keep only last 100)
            if (deletedNotes.length > 100) {
              const recentDeletedNotes = deletedNotes.slice(-50)
              localStorage.setItem('deletedSensitiveNotes', JSON.stringify(recentDeletedNotes))
              console.log('🧹 NotesContext: Cleaned up deleted notes list')
            }
            
            console.log(`🔍 NotesContext: Showing ${validNotes.length} valid notes (${sensitiveNotesInRegular.length - validNotes.length} were deleted)`)
            
            setNotes(validNotes)
            setPagination(prev => ({
              ...prev,
              page: 0,
              totalElements: validNotes.length,
              totalPages: 1
            }))
            return
          }
        } catch (error) {
          console.warn('⚠️ NotesContext: Failed to check regular storage for migration:', error)
        }
      }
      
      setNotes(encryptedNotes)
      
      // Update pagination
      setPagination(prev => ({
        ...prev,
        page: 0,
        totalElements: encryptedNotes.length,
        totalPages: 1 // Sensitive notes don't have pagination
      }))
      
    } catch (error) {
      console.error('❌ NotesContext: Failed to load encrypted sensitive notes:', error)
      setNotes([])
    }
  }

  /**
   * Refresh sensitive notes from local storage (with decryption)
   */
  const refreshSensitiveNotes = async (password = null) => {
    if (!currentFolder || currentFolder.folderType !== 'SENSITIVE') {
      console.log('⚠️ NotesContext: refreshSensitiveNotes called for non-sensitive folder')
      return
    }
    
    // Use provided password or fall back to session password
    const passwordToUse = password || sensitiveSession.sessionPassword
    
    if (!passwordToUse) {
      console.log('⚠️ NotesContext: refreshSensitiveNotes called without authentication')
      return
    }
    
    try {
      console.log('🔄 NotesContext: Refreshing sensitive notes from local storage')
      
      const sensitiveNotes = await sensitiveNotesService.getNotes(
        currentFolder.folderId,
        passwordToUse
      )
      
      console.log(`✅ NotesContext: Refreshed ${sensitiveNotes.length} sensitive notes`)
      setNotes(sensitiveNotes)
      
      // Update pagination
      setPagination(prev => ({
        ...prev,
        page: 0,
        totalElements: sensitiveNotes.length,
        totalPages: 1,
        size: prev.size
      }))
      
    } catch (error) {
      console.error('❌ NotesContext: Failed to refresh sensitive notes:', error)
      // Don't throw - this is a background operation
    }
  }

  const retryInitialSync = async () => {
    try {
      // Clear failed sync timestamp to allow immediate retry
      localStorage.removeItem('lastFailedSync')
      localStorage.setItem('lastForcedSync', Date.now().toString())
      console.log('🔄 NotesContext: Retrying initial sync...')
      
      // First, test the connection to see if it's an auth issue
      if (!shouldUseFrontendSync('read')) {
        try {
          await testGoogleDriveConnection()
        } catch (connectionError) {
          console.log('🔄 NotesContext: Connection test failed, checking for auth issues...')
          
          if (isGoogleDriveTokenExpiredError(connectionError)) {
            console.log('🔄 NotesContext: Detected authentication issue during manual refresh')
            handleGoogleDriveTokenExpiration()
            return false // Don't proceed with sync, user needs to re-authenticate
          }
        }
      } else {
        console.log('🚫 NotesContext: Skipping backend connection test in frontend-only mode')
      }
      
      // If frontend-only mode is enabled and initialized, force refresh via frontend sync
      if (shouldUseFrontendSync('read') && frontendSync.isInitialized) {
        try {
          // Refresh folders first to ensure cross-device folder visibility
          try { await fetchFolders() } catch (_) {}
          
          const result = await frontendSync.loadNotes({ forceRefresh: true, pageSize: 100 })
          if (result?.notes) {
            updateStoredNotes(result.notes)
            setPagination(prev => ({
              ...prev,
              totalElements: result.notes.length,
              totalPages: Math.ceil((result.notes.length || 0) / prev.size)
            }))
          }
          return true
        } catch (e) {
          console.warn('⚠️ NotesContext: Frontend sync refresh failed in retry, falling back to backend fetchNotes', e)
        }
      }
      return await fetchNotes({ forceSync: true })
      
    } catch (error) {
      console.error('❌ NotesContext: Retry initial sync failed:', error)
      return false
    }
  }

  /**
   * Test Google Drive connection to detect authentication issues
   */
  const testGoogleDriveConnection = async () => {
    try {
      console.log('🔍 NotesContext: Testing Google Drive connection...')
      
      // Try to fetch a minimal amount of data to test the connection
      const testResponse = await notesAPI.getNotes({ 
        page: 0, 
        size: 1, // Just one note to test
        metadataOnly: true 
      })
      
      console.log('✅ NotesContext: Google Drive connection test successful')
      return testResponse
      
    } catch (error) {
      console.warn('⚠️ NotesContext: Google Drive connection test failed:', error)
      throw error
    }
  }

  /**
   * Upload offline-created notes to server when connection is restored
   */
  const uploadOfflineNotes = async (cachedNotes) => {
    if (isLocalMode || !navigator.onLine) return
    
    try {
      // Find offline notes that need to be uploaded
      const offlineNotesToUpload = cachedNotes.filter(note => 
        note.isOfflineCreated && note.pendingSync
      )
      
      if (offlineNotesToUpload.length === 0) {
        return // No offline notes to upload
      }
      
      console.log(`⬆️ NotesContext: Uploading ${offlineNotesToUpload.length} offline notes to server...`)
      
      for (const offlineNote of offlineNotesToUpload) {
        try {
          console.log(`⬆️ NotesContext: Uploading offline note: ${offlineNote.title}`)
          
          // Create the note data for server (remove offline-specific fields)
          const { noteId, isOfflineCreated, pendingSync, cachedAt, cacheType, isOptimistic, uploading, justUploaded, ...noteData } = offlineNote
          
          // Ensure title is valid for server validation
          if (!noteData.title || noteData.title.trim() === '') {
            noteData.title = 'Untitled Note'
            console.log(`⚠️ NotesContext: Empty title detected, using default title for note: ${noteData.title}`)
          }
          
          console.log(`🔍 NotesContext: Note data being sent to server:`, JSON.stringify(noteData, null, 2))
          
          // Upload to server
          const serverNote = await notesAPI.createNote(noteData)
          
          if (serverNote && serverNote.noteId) {
            console.log(`✅ NotesContext: Successfully uploaded offline note, server ID: ${serverNote.noteId}`)
            
            // Update the note in IndexedDB with server data
            await updateOfflineNoteWithServerData(offlineNote.noteId, serverNote)
            
            // Update in current state
            setNotes(prev => prev.map(note => 
              note.noteId === offlineNote.noteId
                ? { ...serverNote, justUploaded: true }
                : note
            ))
            
            toast.success(`📤 "${offlineNote.title}" uploaded successfully!`)
          }
          
        } catch (error) {
          console.error(`❌ NotesContext: Failed to upload offline note ${offlineNote.title}:`, error)
          
          // Mark upload as failed but keep note offline
          await markOfflineNoteUploadFailed(offlineNote.noteId, error.message)
        }
      }
      
    } catch (error) {
      console.error('❌ NotesContext: Error during offline notes upload:', error)
    }
  }

  /**
   * Update offline note with server data after successful upload
   */
  const updateOfflineNoteWithServerData = async (offlineNoteId, serverNote) => {
    try {
      const db = await openCloudCacheDB()
      const transaction = db.transaction(['cloud_notes'], 'readwrite')
      const store = transaction.objectStore('cloud_notes')
      
      // Remove the old offline note
      await store.delete(offlineNoteId)
      
      // Add the server note
      const cacheNote = {
        ...serverNote,
        cachedAt: new Date().toISOString(),
        cacheType: 'cloud'
      }
      await store.put(cacheNote)
      
      console.log(`💾 NotesContext: Replaced offline note ${offlineNoteId} with server note ${serverNote.noteId}`)
    } catch (error) {
      console.warn('⚠️ NotesContext: Failed to update cached note after upload:', error)
    }
  }

  /**
   * Mark offline note upload as failed
   */
  const markOfflineNoteUploadFailed = async (offlineNoteId, errorMessage) => {
    try {
      const db = await openCloudCacheDB()
      const transaction = db.transaction(['cloud_notes'], 'readwrite')
      const store = transaction.objectStore('cloud_notes')
      
      // Get the note
      const note = await new Promise((resolve, reject) => {
        const request = store.get(offlineNoteId)
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
      })
      
      if (note) {
        // Update with failure info
        const updatedNote = {
          ...note,
          uploadFailed: true,
          uploadError: errorMessage,
          lastUploadAttempt: new Date().toISOString()
        }
        await store.put(updatedNote)
        console.log(`⚠️ NotesContext: Marked offline note ${offlineNoteId} as upload failed`)
      }
    } catch (error) {
      console.warn('⚠️ NotesContext: Failed to mark note upload as failed:', error)
    }
  }

  /**
   * Cache notes in IndexedDB for cloud users (offline access)
   */
  const cacheNotesInIndexedDB = async (notesToCache) => {
    if (isLocalMode) return // Don't cache if in local mode (already using IndexedDB)
    
    try {
      // Use a separate store for cloud cache to avoid conflicts with local mode
      const db = await openCloudCacheDB()
      const transaction = db.transaction(['cloud_notes'], 'readwrite')
      const store = transaction.objectStore('cloud_notes')
      
      // Get existing cached notes to preserve offline-created notes
      const existingNotes = await new Promise((resolve, reject) => {
        const request = store.getAll()
        request.onsuccess = () => resolve(request.result || [])
        request.onerror = () => reject(request.error)
      })
      
      // Find offline-created notes (notes that exist locally but not on server)
      const serverNoteIds = new Set(notesToCache.map(note => note.noteId))
      const offlineNotes = existingNotes.filter(note => {
        const noteId = note.noteId
        return noteId && !serverNoteIds.has(noteId) && 
               // Only preserve recent offline notes (within last 24 hours)
               note.cachedAt && (Date.now() - new Date(note.cachedAt).getTime()) < (24 * 60 * 60 * 1000)
      })
      
      if (offlineNotes.length > 0) {
        console.log(`💾 NotesContext: Preserving ${offlineNotes.length} offline-created notes during sync`)
      }
      
      // Clear existing cache and store fresh notes + preserved offline notes
      await store.clear()
      
      // Cache server notes first
      for (const note of notesToCache) {
        const cacheNote = {
          ...note,
          cachedAt: new Date().toISOString(),
          cacheType: 'cloud'
        }
        await store.put(cacheNote)
      }
      
      // Re-add preserved offline notes
      for (const offlineNote of offlineNotes) {
        await store.put({
          ...offlineNote,
          // Keep original cachedAt to track when it was created offline
          isOfflineCreated: true
        })
      }
      
      console.log(`💾 NotesContext: Cached ${notesToCache.length} server notes + ${offlineNotes.length} offline notes in cloud cache DB`)
    } catch (error) {
      console.warn('⚠️ NotesContext: Failed to cache notes in IndexedDB:', error)
    }
  }

  /**
   * Get cached cloud notes from IndexedDB (for offline fallback)
   */
  const getCachedCloudNotes = async () => {
    if (isLocalMode) return []
    
    try {
      const db = await openCloudCacheDB()
      const transaction = db.transaction(['cloud_notes'], 'readonly')
      const store = transaction.objectStore('cloud_notes')
      const request = store.getAll()
      
      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const cachedNotes = request.result || []
          console.log(`💾 NotesContext: Retrieved ${cachedNotes.length} cached cloud notes`)
          resolve(cachedNotes)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.warn('⚠️ NotesContext: Failed to get cached notes:', error)
      return []
    }
  }

  /**
   * Cache a single note in IndexedDB
   */
  const cacheSingleNote = async (note) => {
    if (isLocalMode) return
    
    try {
      const db = await openCloudCacheDB()
      const transaction = db.transaction(['cloud_notes'], 'readwrite')
      const store = transaction.objectStore('cloud_notes')
      
      const cacheNote = {
        ...note,
        cachedAt: new Date().toISOString(),
        cacheType: 'cloud'
      }
      
      await store.put(cacheNote)
      console.log(`💾 NotesContext: Cached note ${note.noteId} in cloud cache DB`)
    } catch (error) {
      console.warn('⚠️ NotesContext: Failed to cache note:', error)
    }
  }

  /**
   * Check if background sync should be performed based on cache age and other factors
   * Enhanced for smart folder navigation to minimize unnecessary network calls
   */
  const shouldPerformBackgroundSync = async (cachedNotes) => {
    if (isLocalMode) return false // Never sync in local mode
    
    try {
      const now = Date.now()
      
      // ENHANCED: Check if this is just a folder navigation (not a data refresh)
      const lastFolderChange = localStorage.getItem('lastFolderChange')
      const folderChangeThreshold = 2 * 60 * 1000 // 2 minutes
      
      if (lastFolderChange && (now - parseInt(lastFolderChange)) < folderChangeThreshold) {
        console.log('⚡ NotesContext: Recent folder change detected, skipping background sync for better UX')
        return false
      }
      
      if (!cachedNotes || cachedNotes.length === 0) {
        // Check if this is a fresh session and we've already tried recently
        const lastFailedSync = localStorage.getItem('lastFailedSync')
        const failedSyncCooldown = 2 * 60 * 1000 // 2 minutes cooldown after failed sync
        
        if (lastFailedSync && (now - parseInt(lastFailedSync)) < failedSyncCooldown) {
          console.log('⏳ NotesContext: Recent sync failed, waiting before retry')
          return false // Don't retry immediately after failure
        }
        
        console.log('🔄 NotesContext: No cached notes, performing initial sync')
        return true // Always sync if no cache (but with cooldown)
      }
      
      // ENHANCED: More aggressive cache freshness for folder navigation
      // For folder changes, we can be more lenient with cache age
      const cacheStaleThreshold = 15 * 60 * 1000 // 15 minutes (increased from 10)
      
      // Check if any cached note is stale
      const stalestNote = cachedNotes.reduce((oldest, note) => {
        const noteAge = now - new Date(note.cachedAt).getTime()
        const oldestAge = now - new Date(oldest.cachedAt).getTime()
        return noteAge > oldestAge ? note : oldest
      })
      
      const cacheAge = now - new Date(stalestNote.cachedAt).getTime()
      
      if (cacheAge > cacheStaleThreshold) {
        console.log(`🔄 NotesContext: Cache is ${Math.round(cacheAge / 60000)} minutes old, needs refresh`)
        return true
      }
      
      // Check for forced refresh (user action)
      const lastSyncTime = localStorage.getItem('lastForcedSync')
      const forcedSyncThreshold = 30 * 1000 // 30 seconds
      if (lastSyncTime && (now - parseInt(lastSyncTime)) < forcedSyncThreshold) {
        console.log('🔄 NotesContext: Recent forced refresh, performing sync')
        return true
      }
      
      // Skip sync if called immediately after note creation
      const lastNoteCreation = localStorage.getItem('lastNoteCreation')
      const noteCreationThreshold = 5 * 1000 // 5 seconds
      if (lastNoteCreation && (now - parseInt(lastNoteCreation)) < noteCreationThreshold) {
        console.log('⚡ NotesContext: Recent note creation, skipping background sync')
        return false
      }
      
      // ENHANCED: Check if we have sufficient notes for the current folder
      // If we have enough notes, we can skip sync even if cache is slightly old
      const currentFolderId = currentFolder?.folderId
      if (currentFolderId && currentFolderId !== 'all') {
        const folderNotes = cachedNotes.filter(note => (note.folderId || null) === currentFolderId)
        if (folderNotes.length >= 5) { // If we have at least 5 notes for this folder
          console.log(`⚡ NotesContext: Sufficient notes (${folderNotes.length}) for current folder, cache is fresh enough`)
          return false
        }
      }
      
      // Cache is fresh, skip sync
      console.log(`⚡ NotesContext: Cache is fresh (${Math.round(cacheAge / 60000)} min old), skipping sync`)
      return false
      
    } catch (error) {
      console.warn('⚠️ NotesContext: Error checking cache staleness, defaulting to sync:', error)
      return true // Default to sync on error
    }
  }

  /**
   * Get a single cached note by ID from IndexedDB
   */
  const getCachedNoteById = async (noteId) => {
    if (isLocalMode) return null
    
    // In frontend-only mode, prefer frontend sync cache first (faster and unified)
    if (shouldUseFrontendSync('read')) {
      try {
        const fsCached = await frontendSync.getCachedNoteById(noteId)
        if (fsCached) return fsCached
      } catch (_) {}
    }

    try {
      const db = await openCloudCacheDB()
      const transaction = db.transaction(['cloud_notes'], 'readonly')
      const store = transaction.objectStore('cloud_notes')
      const request = store.get(noteId)
      
      // Add a timeout so UI never hangs indefinitely if IDB stalls
      const TIMEOUT_MS = 2000
      let settled = false
      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          if (settled) return
          settled = true
          const cachedNote = request.result
          if (cachedNote) {
            console.log(`💾 NotesContext: Retrieved cached note ${noteId}`)
            resolve(cachedNote)
          } else {
            console.log(`💾 NotesContext: Note ${noteId} not found in cache`)
            resolve(null)
          }
        }
        request.onerror = () => {
          if (settled) return
          settled = true
          reject(request.error)
        }
        setTimeout(() => {
          if (!settled) {
            console.warn('⏳ NotesContext: getCachedNoteById timed out, falling back')
            settled = true
            resolve(null)
          }
        }, TIMEOUT_MS)
      })
    } catch (error) {
      console.warn('⚠️ NotesContext: Failed to get cached note:', error)
      return null
    }
  }

  /**
   * Remove a single note from IndexedDB cache
   */
  const removeCachedNote = async (noteId) => {
    // Always remove from cache regardless of mode - this is for cleanup
    try {
      console.log(`🔍 NotesContext: removeCachedNote: Starting removal for note ${noteId}`)
      console.log(`🔍 NotesContext: removeCachedNote: isLocalMode = ${isLocalMode}`)
      
      if (isLocalMode) {
        console.log(`🔍 NotesContext: removeCachedNote: Local mode - skipping cloud cache removal`)
        return
      }
      
      console.log(`🔍 NotesContext: removeCachedNote: Not in local mode, proceeding with cloud cache removal...`)
      
      console.log(`🔍 NotesContext: removeCachedNote: About to open cloud cache database...`)
      let db
      try {
        console.log(`🔍 NotesContext: removeCachedNote: Opening database with timeout...`)
        db = await Promise.race([
          openCloudCacheDB(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Database open timeout after 5 seconds')), 5000)
          )
        ])
        console.log(`🔍 NotesContext: removeCachedNote: Database opened successfully`)
      } catch (dbError) {
        console.error('❌ NotesContext: removeCachedNote: Failed to open database:', dbError)
        throw dbError
      }
      
      console.log(`🔍 NotesContext: removeCachedNote: About to create transaction...`)
      const transaction = db.transaction(['cloud_notes'], 'readwrite')
      console.log(`🔍 NotesContext: removeCachedNote: Transaction created successfully`)
      
      console.log(`🔍 NotesContext: removeCachedNote: About to access object store...`)
      const store = transaction.objectStore('cloud_notes')
      console.log(`🔍 NotesContext: removeCachedNote: Object store accessed successfully`)
      
      console.log(`🔍 NotesContext: removeCachedNote: About to delete note from store...`)
      await store.delete(noteId)
      console.log(`💾 NotesContext: Removed note ${noteId} from cloud cache DB`)
    } catch (error) {
      console.error('❌ NotesContext: removeCachedNote failed with error:', error)
      console.error('❌ NotesContext: removeCachedNote error stack:', error.stack)
      // Re-throw the error so the calling function can handle it
      throw error
    }
  }

  /**
   * Check for notes deleted on other devices and remove them from local cache
   */
  const syncDeletedNotes = async () => {
    if (isLocalMode) return
    
    // Skip if frontend sync is handling everything
    if (shouldUseFrontendSync('read')) {
      console.log('🚀 NotesContext: Frontend sync enabled - skipping backend deleted notes check')
      return
    }
    
    try {
      console.log('🔄 NotesContext: Checking for notes deleted on other devices...')
      
      // Get list of deleted note IDs from server
       const response = await notesAPI.getDeletedNotes()
       const deletedNoteIds = response || []
      
      if (deletedNoteIds.length === 0) {
        console.log('✅ NotesContext: No deleted notes to sync')
        return
      }
      
      console.log(`🗑️ NotesContext: Found ${deletedNoteIds.length} notes deleted on other devices`)
      
      // Remove deleted notes from local state
      setNotes(prev => {
        const filteredNotes = prev.filter(note => !deletedNoteIds.includes(note.noteId || note.id))
        const removedCount = prev.length - filteredNotes.length
        if (removedCount > 0) {
          console.log(`🗑️ NotesContext: Removed ${removedCount} deleted notes from state`)
        }
        return filteredNotes
      })
      
      // Remove deleted notes from cache
      const db = await openCloudCacheDB()
      const transaction = db.transaction(['cloud_notes'], 'readwrite')
      const store = transaction.objectStore('cloud_notes')
      
      let removedFromCache = 0
      for (const noteId of deletedNoteIds) {
        try {
          await store.delete(noteId)
          removedFromCache++
        } catch (error) {
          console.warn(`⚠️ NotesContext: Failed to remove cached note ${noteId}:`, error)
        }
      }
      
      if (removedFromCache > 0) {
        console.log(`💾 NotesContext: Removed ${removedFromCache} deleted notes from cache`)
      }
      
    } catch (error) {
      console.warn('⚠️ NotesContext: Failed to sync deleted notes:', error)
    }
  }

  /**
   * Check for notes updated on other devices and refresh them
   */
  const syncUpdatedNotes = async () => {
    if (isLocalMode) return
    
    // Skip if frontend sync is handling everything
    if (shouldUseFrontendSync('read')) {
      console.log('🚀 NotesContext: Frontend sync enabled - skipping backend updated notes check')
      return
    }
    
    try {
      console.log('🔄 NotesContext: Checking for notes updated on other devices...')
      
      // Get list of updated note information from server
      const response = await notesAPI.getUpdatedNotes()
      const { updatedNotes = [], count = 0 } = response
      
      if (count === 0) {
        console.log('✅ NotesContext: No updated notes to sync')
        return
      }
      
      console.log(`📝 NotesContext: Found ${count} notes updated on other devices`)
      
      // Group updates by noteId and keep only the latest
      const latestUpdates = new Map()
      updatedNotes.forEach(update => {
        const existing = latestUpdates.get(update.noteId)
        if (!existing || new Date(update.updatedAt) > new Date(existing.updatedAt)) {
          latestUpdates.set(update.noteId, update)
        }
      })
      
      let refreshedCount = 0
      let conflictCount = 0
      
      // Process each updated note
      for (const [noteId, updateInfo] of latestUpdates) {
        try {
          // Check if we have this note locally
          const localNote = notes.find(note => note.noteId === noteId)
          
          if (localNote) {
            // Check for version conflicts
            const localVersion = localNote.version || '1'
            const cloudVersion = updateInfo.version
            
            if (localVersion !== cloudVersion) {
              console.log(`🔄 NotesContext: Version mismatch for note ${noteId}. Local: ${localVersion}, Cloud: ${cloudVersion}`)
              
              // Check if local note was modified recently (within last hour)
              const lastModified = localNote.lastModified || localNote.updatedAt
              const isRecentlyModified = lastModified && 
                (Date.now() - new Date(lastModified).getTime()) < (60 * 60 * 1000)
              
              if (isRecentlyModified) {
                console.warn(`⚠️ NotesContext: Conflict detected for note ${noteId}. Skipping auto-sync to prevent data loss.`)
                conflictCount++
                // TODO: Implement conflict resolution UI
                continue
              }
              
              // Fetch the latest version from server
              try {
                const freshNote = await notesAPI.getNoteById(noteId)
                
                // Update local state with fresh version
                        setNotes(prev => prev.map(note => 
          note.noteId === noteId ? freshNote : note
        ))
                
                // Update cache
                await cacheSingleNote(freshNote)
                refreshedCount++
                
                console.log(`🔄 NotesContext: Refreshed note ${noteId} to version ${freshNote.version || 'unknown'}`)
                
              } catch (fetchError) {
                console.warn(`⚠️ NotesContext: Failed to fetch updated note ${noteId}:`, fetchError)
              }
            }
          } else {
            // Note doesn't exist locally, might be a new note created on another device
            try {
              const newNote = await notesAPI.getNoteById(noteId)
              
              // Add to local state
              setNotes(prev => [...prev, newNote])
              
              // Add to cache
              await cacheSingleNote(newNote)
              refreshedCount++
              
              console.log(`➕ NotesContext: Added new note ${noteId} from other device`)
              
            } catch (fetchError) {
              console.warn(`⚠️ NotesContext: Failed to fetch new note ${noteId}:`, fetchError)
            }
          }
          
        } catch (error) {
          console.warn(`⚠️ NotesContext: Failed to process update for note ${noteId}:`, error)
        }
      }
      
      if (refreshedCount > 0) {
        console.log(`📝 NotesContext: Refreshed ${refreshedCount} notes from other devices`)
      }
      
      if (conflictCount > 0) {
        console.warn(`⚠️ NotesContext: ${conflictCount} notes have conflicts that need manual resolution`)
        // TODO: Show conflict notification to user
      }
      
    } catch (error) {
      console.warn('⚠️ NotesContext: Failed to sync updated notes:', error)
    }
  }

  /**
   * Open or create the cloud cache IndexedDB
   */
  const openCloudCacheDB = () => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('CloudNotesCache', 2) // Increment version for new indices
      
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result)
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result
        if (!db.objectStoreNames.contains('cloud_notes')) {
          const store = db.createObjectStore('cloud_notes', { keyPath: 'noteId' })
          // Add search-optimized indices for faster text search
          store.createIndex('cachedAt', 'cachedAt')
          store.createIndex('title', 'title')
          store.createIndex('folderId', 'folderId')
          store.createIndex('lastModified', 'lastModified')
          // Note: content search will be done via filter since IndexedDB doesn't support full-text search
          console.log('📊 NotesContext: Created IndexedDB with search-optimized indices')
        }
      }
    })
  }

  /**
   * Real-time search as you type (debounced with cancellation)
   */
  const searchAsYouType = async (searchTerm, folderId = null) => {
    // Clear previous debounce timer
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer)
    }

    // Cancel any ongoing search request
    if (currentSearchController) {
      currentSearchController.abort()
    }

    // Update search state immediately for UI responsiveness
    setCurrentSearchTerm(searchTerm)
    
    console.log('🔍 NotesContext: searchAsYouType called with:', {
      searchTerm: `"${searchTerm}"`,
      trimmed: `"${searchTerm.trim()}"`,
      isEmpty: !searchTerm.trim(),
      folderId,
      currentSearchMode: isSearchMode
    })
    
    if (!searchTerm.trim()) {
      // If search term is empty, exit search mode and restore original notes
      console.log('🔍 NotesContext: Empty search term, clearing search mode')
      setIsSearchMode(false)
      const filtered = currentFolder && !isShowingAllNotes
        ? allNotes.filter(n => (n.folderId || null) === currentFolder.folderId)
        : allNotes
      setNotes(filtered)
      setSearchLoading(false)
      console.log('🔍 NotesContext: Search cleared due to empty term, restored original notes')
      return
    }

    // Enter search mode and show loading
    setIsSearchMode(true)
    setSearchLoading(true)

    // Create new AbortController for this search
    const searchController = new AbortController()
    setCurrentSearchController(searchController)

    // Set up debounced search with performance optimizations
    const newTimer = setTimeout(async () => {
      // Check if search was cancelled
      if (searchController.signal.aborted) {
        return
      }

      try {
        console.log('🔍 NotesContext: Real-time search for:', searchTerm)
        
        if (isLocalMode) {
          // For local mode: instant search through local notes
          const results = await searchLocalNotes(searchTerm)
          
          // Check if search was cancelled after async operation
          if (searchController.signal.aborted) {
            return
          }
          
          // Apply folder filtering if specified
          let filteredResults = results
          if (folderId) {
            if (folderId === 'all') {
              // Show all results (already filtered by searchLocalNotes)
              filteredResults = results
            } else if (folderId === 'root') {
              filteredResults = results.filter(note => !note.folderId)
            } else {
              filteredResults = results.filter(note => note.folderId === folderId)
            }
          }
          
          // Check cancellation one more time before updating state
          if (!searchController.signal.aborted) {
            setNotes(filteredResults)
            setPagination(prev => ({
              ...prev,
              totalElements: filteredResults.length,
              totalPages: Math.ceil(filteredResults.length / prev.size),
              currentPage: 0
            }))
          }
        } else {
          // For cloud mode: use IndexedDB cache first (instant)
          const cachedResults = await searchCachedNotes(searchTerm, folderId)
          
          // Check if search was cancelled
          if (searchController.signal.aborted) {
            return
          }
          
          if (cachedResults !== null) {
            // Success! Use cached results (instant search)
            console.log('⚡ NotesContext: Real-time search using cached results')
            if (!searchController.signal.aborted) {
              setNotes(cachedResults)
              setPagination(prev => ({
                ...prev,
                totalElements: cachedResults.length,
                totalPages: Math.ceil(cachedResults.length / prev.size),
                currentPage: 0
              }))
            }
          } else {
            // Fallback: Use server search (should be rare)
            console.log('🌐 NotesContext: Real-time search fallback to server')
            const searchParams = { 
              term: searchTerm, 
              searchInContent: true, 
              folderId 
            }
            
            try {
              const response = await notesAPI.searchNotes({
                ...searchParams,
                signal: searchController.signal // Pass abort signal to request
              })
              
              if (!searchController.signal.aborted && response) {
                console.log('🔍 NotesContext: Server search response:', {
                  hasNotes: !!response.notes,
                  notesLength: response.notes?.length || 0,
                  totalElements: response.totalElements
                })
                
                // Always set search results, even if empty (to show "no results" instead of clearing search)
                const searchResults = response.notes || []
                setNotes(searchResults)
                setPagination(prev => ({
                  ...prev,
                  totalElements: response.totalElements || searchResults.length,
                  totalPages: response.totalPages || Math.ceil(searchResults.length / prev.size),
                  currentPage: 0
                }))
                
                // Cache results for next time (even if empty, for consistency)
                if (searchResults.length > 0) {
                  await cacheNotesInIndexedDB(searchResults)
                }
                
                console.log(`✅ NotesContext: Real-time search completed with ${searchResults.length} results`)
              }
            } catch (error) {
              if (error.name === 'AbortError') {
                console.log('🔍 NotesContext: Search request cancelled')
                return
              }
              console.error('❌ NotesContext: Real-time search error:', error)
              // Show empty results on error instead of keeping old results
              setNotes([])
              setPagination(prev => ({
                ...prev,
                totalElements: 0,
                totalPages: 0,
                currentPage: 0
              }))
            }
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('🔍 NotesContext: Search operation cancelled')
          return
        }
        console.error('❌ NotesContext: Real-time search error:', error)
      } finally {
        // Only update loading state if search wasn't cancelled
        if (!searchController.signal.aborted) {
          setSearchLoading(false)
          setCurrentSearchController(null)
        }
      }
    }, 300) // 300ms debounce - good balance between responsiveness and performance

    setSearchDebounceTimer(newTimer)
  }

  /**
   * Clear search and return to normal mode
   */
  const clearSearch = () => {
    console.log('🔍 NotesContext: clearSearch() called - stack trace:', new Error().stack)
    
    // Clear debounce timer
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer)
      setSearchDebounceTimer(null)
    }

    // Reset search state
    setIsSearchMode(false)
    setCurrentSearchTerm('')
    setSearchLoading(false)
    
    // Restore original notes with proper folder filtering
    const filtered = currentFolder && !isShowingAllNotes
      ? allNotes.filter(n => (n.folderId || null) === currentFolder.folderId)
      : allNotes
    setNotes(filtered)
    
    console.log('🔍 NotesContext: Search cleared, restored original notes')
  }

  /**
   * Safe wrapper for setNotes that preserves search results
   */
  const safeSetNotes = (newNotesOrUpdater) => {
    if (isSearchMode && currentSearchTerm && currentSearchTerm.trim()) {
      console.log('⚠️ NotesContext: Attempted to update notes during search mode - preserving search results')
      return // Don't update notes during active search
    }
    setNotes(newNotesOrUpdater)
  }

  /**
   * Update stored notes for search filtering
   */
  const updateStoredNotes = (newNotes) => {
    // Dedupe by note ID, prefer newest lastModified; if equal, prefer one with folderId
    const deduped = (() => {
      const byId = new Map()
      for (const note of newNotes || []) {
        const key = note.noteId || note.id
        if (!key) continue
        const existing = byId.get(key)
        if (!existing) {
          byId.set(key, note)
        } else {
          const a = new Date(existing.lastModified || existing.modifiedTime || existing.updatedAt || existing.createdAt || 0).getTime()
          const b = new Date(note.lastModified || note.modifiedTime || note.updatedAt || note.createdAt || 0).getTime()
          if (b > a) {
            byId.set(key, note)
          } else if (b === a) {
            const preferNew = (note.folderId != null && existing.folderId == null)
            if (preferNew) byId.set(key, note)
          }
        }
      }
      return Array.from(byId.values())
    })()

    setAllNotes(deduped)
    if (!isSearchMode) {
      const filtered = currentFolder && !isShowingAllNotes
        ? deduped.filter(n => (n.folderId || null) === currentFolder.folderId)
        : isShowingAllNotes
          ? deduped.filter(n => !isNoteSensitive(n)) // Filter out sensitive notes from "All Notes" view
          : deduped
      setNotes(filtered)
    } else {
      // If in search mode, re-run the current search to ensure results stay filtered
      console.log('🔍 NotesContext: Background update during search mode, re-filtering search results...')
      if (currentSearchTerm && currentSearchTerm.trim()) {
        // Re-apply current search filter to the updated notes
        const folderId = currentFolder && !isShowingAllNotes ? currentFolder.folderId : 'all'
        
        // Re-filter the updated notes using the current search term
        const lowerSearchTerm = currentSearchTerm.toLowerCase().trim()
        let filteredNotes = deduped.filter(note => {
          const titleMatch = note.title && note.title.toLowerCase().includes(lowerSearchTerm)
          const contentMatch = note.content && note.content.toLowerCase().includes(lowerSearchTerm)
          return titleMatch || contentMatch
        })
        
        // Apply folder filtering if needed
        if (folderId === 'all') {
          // Filter out sensitive notes from "All Notes" search results
          filteredNotes = filteredNotes.filter(note => !isNoteSensitive(note))
        } else if (folderId === 'root') {
          filteredNotes = filteredNotes.filter(note => !note.folderId)
        } else {
          filteredNotes = filteredNotes.filter(note => note.folderId === folderId)
        }
        
        // Sort by lastModified
        filteredNotes.sort((a, b) => {
          const dateA = new Date(a.lastModified || a.modifiedTime || a.updatedAt || a.createdAt || 0)
          const dateB = new Date(b.lastModified || b.modifiedTime || b.updatedAt || b.createdAt || 0)
          return dateB - dateA
        })
        
        setNotes(filteredNotes)
        console.log(`✅ NotesContext: Re-filtered search results: ${filteredNotes.length} matches`)
      }
    }
  }

  /**
   * Highlight search terms in text for better UX
   */
  const highlightSearchTerm = (text, searchTerm) => {
    if (!text || !searchTerm) return text
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    return text.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-600 px-1 rounded">$1</mark>')
  }

  /**
   * Get search snippet from content (first occurrence of search term with context)
   */
  const getSearchSnippet = (content, searchTerm, maxLength = 150) => {
    if (!content || !searchTerm) return content?.substring(0, maxLength) + (content?.length > maxLength ? '...' : '')
    
    const lowerContent = content.toLowerCase()
    const lowerSearchTerm = searchTerm.toLowerCase()
    const searchIndex = lowerContent.indexOf(lowerSearchTerm)
    
    if (searchIndex === -1) {
      // Search term not found in content, return beginning
      return content.substring(0, maxLength) + (content.length > maxLength ? '...' : '')
    }
    
    // Calculate snippet boundaries with context
    const contextLength = Math.floor((maxLength - searchTerm.length) / 2)
    const start = Math.max(0, searchIndex - contextLength)
    const end = Math.min(content.length, searchIndex + searchTerm.length + contextLength)
    
    let snippet = content.substring(start, end)
    
    // Add ellipsis if snippet doesn't start/end at text boundaries
    if (start > 0) snippet = '...' + snippet
    if (end < content.length) snippet = snippet + '...'
    
    return snippet
  }

  // Helper to check if there are more notes to load
  const hasMoreNotes = !isLocalMode && !isSearchMode && pagination.totalPages > 0 && pagination.page < pagination.totalPages - 1

  const value = {
    // State
    notes,
    loading,
    pagination,
    allNotes, // All notes without pagination (for search and filters)
    showReconnectionBanner,
    hasMoreNotes, // Helper for UI to know if Load More button should be shown
    
    // Methods
    fetchNotes,
    loadMore, // New load more function
    createNote,
    updateNote,
    deleteNote,
    searchNotes,
    refreshNotes,
    refreshSensitiveNotes, // Refresh sensitive notes from local storage
    loadEncryptedSensitiveNotes, // Load encrypted sensitive notes for list display
    hideReconnectionBanner,
    searchAsYouType, // Expose real-time search function
    clearSearch, // Expose clear search function
    isSearchMode, // Expose search mode state
    currentSearchTerm, // Expose current search term
    searchLoading, // Expose search loading state
    highlightSearchTerm, // Expose search highlighting function
    getSearchSnippet, // Expose search snippet function
    
    // Cache functions for individual note access
    getCachedCloudNotes,
    cacheSingleNote,
    removeCachedNote,
    getCachedNoteById, // Expose new function
    retryInitialSync, // Manual retry for fresh sessions
    retryInitialSyncWithFallback, // Advanced retry with fallback strategies
    testGoogleDriveConnection, // Test connection function
    handleGoogleDriveTokenExpiration, // Force re-authentication
    syncDeletedNotes, // Expose new function
    syncUpdatedNotes, // Expose new function
  }

  return (
    <NotesContext.Provider value={value}>
      {children}
    </NotesContext.Provider>
  )
}

export default NotesContext 